import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import { 
    MessageCircle, X, Send, Bot, MessageSquare, LogIn, Phone, Video, 
    Check, CheckCheck, Mic, Image as ImageIcon, Smile, Square, Trash2, 
    Maximize2, Minimize2, Play, Pause, Headphones, Sparkles, ArrowLeft, 
    ArrowRight, Copy, Volume2, VolumeX, ShoppingBag, ExternalLink, Zap, 
    ShieldCheck, Crown, ChevronRight, ChevronLeft, Store 
} from 'lucide-react';
import WebRTCCallOverlay from './WebRTCCallOverlay';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    API_BASE,
    askAiAssistant,
    getSupportConversation,
    sendSupportMessage,
    startSupportSession,
    uploadChatMedia,
    refreshAuthSession,
    getSessionChatMessages,
    getCustomerChatThreads,
    getC2cChatThreads,
    getOrCreateC2cSession,
    sendChatMessage,
    resolveStorefrontImageUrl,
    getFriendshipStatus,
    sendFriendRequest,
    acceptFriendRequest,
    declineFriendRequest,
    removeFriend,
    deleteC2cChatThread,
    blockUser,
    unblockUser,
    getChatBlockStatus
} from '../api/client';
import ChatProductBubble, { parseProductMessage } from './ChatProductBubble';
import ProductShareModal from './ProductShareModal';
import CustomerSearchModal from './CustomerSearchModal';
import { Users, Plus, MessageSquareText, Search, UserPlus, UserCheck, UserX, Lock, ShieldAlert } from 'lucide-react';
import { isRTL, getCurrentLanguage, getTranslation as t } from '../utils/translations';

const getSupportedMimeType = () => {
    if (typeof MediaRecorder === 'undefined') return 'audio/mp4';
    const types = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/mp4',
        'audio/aac',
        'audio/mpeg'
    ];
    for (const type of types) {
        if (MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(type)) {
            return type;
        }
    }
    return '';
};

const CustomAudioPlayer = ({ src }) => {
    const audioRef = React.useRef(null);
    const [isPlaying, setIsPlaying] = React.useState(false);
    const [currentTime, setCurrentTime] = React.useState(0);
    const [duration, setDuration] = React.useState(0);
    const [speed, setSpeed] = React.useState(1.0);

    const togglePlay = (e) => {
        e.stopPropagation();
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play().catch(err => console.error("Audio play failed", err));
        }
    };

    const cycleSpeed = (e) => {
        e.stopPropagation();
        if (!audioRef.current) return;
        const nextSpeed = speed === 1.0 ? 1.5 : (speed === 1.5 ? 2.0 : 1.0);
        audioRef.current.playbackRate = nextSpeed;
        setSpeed(nextSpeed);
    };

    React.useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onPlay = () => setIsPlaying(true);
        const onPause = () => setIsPlaying(false);
        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onLoadedMetadata = () => setDuration(audio.duration);
        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        audio.addEventListener('play', onPlay);
        audio.addEventListener('pause', onPause);
        audio.addEventListener('timeupdate', onTimeUpdate);
        audio.addEventListener('loadedmetadata', onLoadedMetadata);
        audio.addEventListener('ended', onEnded);

        if (audio.duration) {
            setDuration(audio.duration);
        }

        return () => {
            audio.removeEventListener('play', onPlay);
            audio.removeEventListener('pause', onPause);
            audio.removeEventListener('timeupdate', onTimeUpdate);
            audio.removeEventListener('loadedmetadata', onLoadedMetadata);
            audio.removeEventListener('ended', onEnded);
        };
    }, [src]);

    const formatAudioTime = (sec) => {
        if (isNaN(sec) || !isFinite(sec)) return "0:00";
        const m = Math.floor(sec / 60);
        const s = Math.floor(sec % 60);
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const handleSeek = (ratio, e) => {
        e.stopPropagation();
        if (!audioRef.current || !duration) return;
        const targetTime = ratio * duration;
        audioRef.current.currentTime = targetTime;
        setCurrentTime(targetTime);
    };

    const barHeights = [8, 14, 22, 16, 26, 12, 18, 24, 10, 16, 28, 20, 14, 22, 18, 12, 26, 16, 10, 14];
    const progressRatio = duration > 0 ? (currentTime / duration) : 0;

    return (
        <div 
            className="custom-audio-player" 
            onClick={(e) => e.stopPropagation()}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '6px 10px',
                background: 'rgba(15, 23, 42, 0.75)',
                backdropFilter: 'blur(8px)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                minWidth: '220px',
                maxWidth: '280px'
            }}
        >
            <audio ref={audioRef} src={resolveStorefrontImageUrl(src)} preload="metadata" />
            
            {/* Play/Pause Button */}
            <button 
                type="button" 
                onClick={togglePlay} 
                style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: isPlaying ? 'linear-gradient(135deg, #10b981, #059669)' : 'rgba(255, 255, 255, 0.18)',
                    border: 'none',
                    color: isPlaying ? '#041d14' : '#fff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    transition: 'all 0.2s ease'
                }}
            >
                {isPlaying ? <Pause size={14} fill="currentColor" /> : <Play size={14} fill="currentColor" style={{ marginLeft: '2px' }} />}
            </button>

            {/* Waveform Bars */}
            <div 
                style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '2.5px',
                    height: '30px',
                    cursor: 'pointer'
                }}
            >
                {barHeights.map((h, idx) => {
                    const barRatio = (idx + 0.5) / barHeights.length;
                    const isPlayed = progressRatio >= barRatio;
                    return (
                        <div
                            key={idx}
                            onClick={(e) => handleSeek(idx / (barHeights.length - 1), e)}
                            style={{
                                width: '3px',
                                height: `${h}px`,
                                borderRadius: '1.5px',
                                background: isPlayed ? '#10b981' : 'rgba(255, 255, 255, 0.3)',
                                transition: 'background 0.15s ease',
                                cursor: 'pointer'
                            }}
                        />
                    );
                })}
            </div>

            {/* Time readout */}
            <span style={{ fontSize: '11px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                {isPlaying ? formatAudioTime(currentTime) : (duration ? formatAudioTime(duration) : '0:00')}
            </span>

            {/* Speed Toggle Pill */}
            <button
                type="button"
                onClick={cycleSpeed}
                style={{
                    background: speed > 1 ? 'rgba(16, 185, 129, 0.25)' : 'rgba(255, 255, 255, 0.08)',
                    border: speed > 1 ? '1px solid #10b981' : '1px solid rgba(255, 255, 255, 0.15)',
                    color: speed > 1 ? '#10b981' : '#cbd5e1',
                    borderRadius: '10px',
                    padding: '2px 6px',
                    fontSize: '10px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    flexShrink: 0
                }}
            >
                {speed}x
            </button>
        </div>
    );
};

const renderUniversalChatContent = (rawText, mine) => {
    if (!rawText) return null;
    const cleanText = String(rawText).trim();
    const prodData = parseProductMessage(cleanText);

    if (prodData) {
        return <ChatProductBubble product={prodData} isMine={mine} />;
    }

    // 1. Voice message detection across all platforms
    const isVoice = cleanText.startsWith('[audio]') || cleanText.startsWith('[VOICE:') || cleanText.startsWith('[VOICE: ') ||
        cleanText.startsWith('[AUDIO:') || cleanText.startsWith('[AUDIO: ') || cleanText.startsWith('[voice]') ||
        cleanText.startsWith('🎤') ||
        /\.(webm|m4a|mp3|wav|ogg|aac|3gp)(\s*\])?$/i.test(cleanText);

    if (isVoice) {
        let url = cleanText;
        const tags = ['[audio]', '[VOICE:', '[AUDIO:', '[voice]', '🎤 رسالة صوتية', '🎤'];
        for (const tag of tags) {
            if (url.toLowerCase().includes(tag.toLowerCase())) {
                const idx = url.toLowerCase().indexOf(tag.toLowerCase());
                url = url.substring(idx + tag.length).trim();
            }
        }
        url = url.replace(/\]\s*$/, '').trim();
        const finalAudioUrl = resolveStorefrontImageUrl(url);
        return <CustomAudioPlayer src={finalAudioUrl} />;
    }

    // 2. Image message detection across all platforms
    const isImg = cleanText.startsWith('[image]') || cleanText.startsWith('[IMAGE:') || cleanText.startsWith('[IMAGE: ') ||
        cleanText.startsWith('[image:') || cleanText.startsWith('📷') ||
        /\.(jpg|jpeg|png|webp|gif)(\s*\])?$/i.test(cleanText);

    if (isImg) {
        let url = cleanText;
        const tags = ['[image]', '[IMAGE:', '[image:', '📷 صورة من المعرض', '📷 صورة ملتقطة بالكاميرا', '📷 صورة', '📷'];
        for (const tag of tags) {
            if (url.toLowerCase().includes(tag.toLowerCase())) {
                const idx = url.toLowerCase().indexOf(tag.toLowerCase());
                url = url.substring(idx + tag.length).trim();
            }
        }
        url = url.replace(/\]\s*$/, '').trim();
        const finalImgUrl = resolveStorefrontImageUrl(url);
        return (
            <div style={{ position: 'relative', borderRadius: 8, overflow: 'hidden' }}>
                <img 
                    src={finalImgUrl} 
                    alt="attachment" 
                    style={{ maxWidth: '100%', maxHeight: '240px', borderRadius: '8px', cursor: 'pointer', display: 'block', objectFit: 'cover' }} 
                    onClick={() => window.open(finalImgUrl, '_blank')} 
                    onError={(e) => {
                        console.warn("Failed to load chat image:", finalImgUrl);
                    }}
                />
            </div>
        );
    }

    // 3. Sticker detection
    if ((cleanText.startsWith('[STICKER:') || cleanText.startsWith('[STICKER: ')) && cleanText.endsWith(']')) {
        const prefixLen = cleanText.startsWith('[STICKER: ') ? 10 : 9;
        const stickerVal = cleanText.substring(prefixLen, cleanText.length - 1).trim();
        if (stickerVal.startsWith('http') || stickerVal.startsWith('/') || stickerVal.includes('.png') || stickerVal.includes('.jpg') || stickerVal.includes('.webp')) {
            return <img src={resolveStorefrontImageUrl(stickerVal.replace(/\/+$/, ''))} alt="sticker" style={{ width: '80px', height: '80px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />;
        } else {
            return <span style={{ fontSize: '36px', lineHeight: 1.2, display: 'inline-block', padding: '4px' }}>{stickerVal}</span>;
        }
    } else if (/^\/?assets\/stickers\/.*\.(png|jpg|jpeg|webp|gif)/i.test(cleanText) || cleanText.includes('assets/stickers/')) {
        const cleanSrc = cleanText.replace(/\/+$/, '').replace(/^[^\/]*assets/, '/assets');
        return <img src={resolveStorefrontImageUrl(cleanSrc)} alt="sticker" style={{ width: '80px', height: '80px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />;
    }

    // 4. Default plain text
    return <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{rawText}</div>;
};

const playSound = (type, enabled = true) => {
    if (!enabled) return;
    try {
        const AudioContextClass = window.AudioContext || window.webkitAudioContext;
        if (!AudioContextClass) return;
        const ctx = new AudioContextClass();
        
        if (ctx.state === 'suspended') {
            ctx.resume();
        }

        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        
        osc.connect(gain);
        gain.connect(ctx.destination);
        
        const now = ctx.currentTime;
        
        if (type === 'sent') {
            osc.type = 'sine';
            osc.frequency.setValueAtTime(587.33, now); // D5
            osc.frequency.exponentialRampToValueAtTime(1174.66, now + 0.15); // D6
            
            gain.gain.setValueAtTime(0.1, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
            
            osc.start(now);
            osc.stop(now + 0.15);
        } else if (type === 'received') {
            osc.type = 'sine';
            osc.frequency.setValueAtTime(880, now); // A5
            osc.frequency.exponentialRampToValueAtTime(587.33, now + 0.15); // D5
            
            gain.gain.setValueAtTime(0.1, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
            
            osc.start(now);
            osc.stop(now + 0.15);
            
            setTimeout(() => {
                try {
                    const ctx2 = new AudioContextClass();
                    const osc2 = ctx2.createOscillator();
                    const gain2 = ctx2.createGain();
                    osc2.connect(gain2);
                    gain2.connect(ctx2.destination);
                    osc2.type = 'sine';
                    const now2 = ctx2.currentTime;
                    osc2.frequency.setValueAtTime(783.99, now2); // G5
                    osc2.frequency.exponentialRampToValueAtTime(987.77, now2 + 0.15); // B5
                    gain2.gain.setValueAtTime(0.08, now2);
                    gain2.gain.exponentialRampToValueAtTime(0.001, now2 + 0.15);
                    osc2.start(now2);
                    osc2.stop(now2 + 0.15);
                } catch (_) {}
            }, 100);
        } else if (type === 'error') {
            osc.type = 'sawtooth';
            osc.frequency.setValueAtTime(150, now);
            osc.frequency.linearRampToValueAtTime(100, now + 0.25);
            
            gain.gain.setValueAtTime(0.15, now);
            gain.gain.exponentialRampToValueAtTime(0.001, now + 0.25);
            
            osc.start(now);
            osc.stop(now + 0.25);
        }
    } catch (_) {}
};

const triggerVibration = (type) => {
    try {
        if (typeof window !== 'undefined' && window.navigator && window.navigator.vibrate) {
            if (type === 'sent') {
                window.navigator.vibrate(40);
            } else if (type === 'received') {
                window.navigator.vibrate([60, 40, 60]);
            } else if (type === 'error') {
                window.navigator.vibrate([120, 80, 120]);
            }
        }
    } catch (_) {}
};

const formatAiMarkdown = (content) => {
    if (!content) return '';
    const lines = content.split('\n');
    return lines.map((line, lineIdx) => {
        const trimmed = line.trim();
        const isBullet = trimmed.startsWith('•') || trimmed.startsWith('-');
        const isHeader = trimmed.startsWith('##') || trimmed.startsWith('#');
        
        let cleanText = line;
        if (isHeader) {
            cleanText = cleanText.replace(/^#+\s*/, '');
        }

        const parts = cleanText.split(/(\*\*.*?\*\*)/g);
        const rendered = parts.map((part, pIdx) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return (
                    <strong key={pIdx} style={{ color: '#F8FAFC', fontWeight: 700 }}>
                        {part.slice(2, -2)}
                    </strong>
                );
            }
            return part;
        });

        return (
            <div 
                key={lineIdx} 
                style={{ 
                    marginBottom: trimmed === '' ? '6px' : '3px',
                    paddingLeft: isBullet ? '8px' : '0',
                    fontWeight: isHeader ? 700 : 'normal',
                    fontSize: isHeader ? '13.5px' : 'inherit',
                    color: isHeader ? '#D4AF37' : 'inherit',
                    lineHeight: '1.6'
                }}
            >
                {rendered}
            </div>
        );
    });
};

const C2C_TAB = 'c2c';
const SUPPORT_TAB = 'support';
const AI_TAB = 'ai';

const ChatWidget = () => {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [activeTab, setActiveTab] = useState(SUPPORT_TAB);
    const [input, setInput] = useState('');
    const [activeOrderId, setActiveOrderId] = useState(null);
    const [soundEnabled, setSoundEnabled] = useState(true);
    const [copiedMsgId, setCopiedMsgId] = useState(null);
    const [productContext, setProductContext] = useState(null);
    const [isSellerMode, setIsSellerMode] = useState(false);
    const [activeSellerInfo, setActiveSellerInfo] = useState(null);

    // C2C Direct Messaging States
    const [c2cThreads, setC2cThreads] = useState([]);
    const [c2cThreadsLoading, setC2cThreadsLoading] = useState(false);
    const [activeC2cSession, setActiveC2cSession] = useState(null);
    const [c2cMessages, setC2cMessages] = useState([]);
    const [showProductShareModal, setShowProductShareModal] = useState(false);
    const [showCustomerSearchModal, setShowCustomerSearchModal] = useState(false);
    const [c2cSearchFilter, setC2cSearchFilter] = useState('');

    // C2C Mutual Friendship & Calling State
    const [friendshipStatus, setFriendshipStatus] = useState({ isFriend: false, canCall: false, status: 'NONE' });
    const [friendshipLoading, setFriendshipLoading] = useState(false);
    const [showFriendshipModal, setShowFriendshipModal] = useState(false);

    const [sessionId, setSessionId] = useState(null);
    const [supportMessages, setSupportMessages] = useState([]);
    const [supportTyping, setSupportTyping] = useState(false);
    const typingTimeoutRef = useRef(null);
    const lastTypedRef = useRef(0);

    // WebRTC Calling State
    const [callStatus, setCallStatus] = useState('idle');
    const [callType, setCallType] = useState('video');
    const [remoteName, setRemoteName] = useState('Support Agent');
    const [stompClientInstance, setStompClientInstance] = useState(null);

    const [aiMessages, setAiMessages] = useState([
        {
            id: `ai-welcome-initial`,
            role: 'assistant',
            text: isRTL() 
                ? 'مرحباً بك في LUXVONO. أنا مستشارك الرقمي الخاص، كيف يمكنني إرشادك اليوم بخصوص مجموعاتنا الفاخرة أو حالة طلبيتك؟'
                : 'Welcome to LUXVONO. I am your Sovereign AI Digital Advisor. How may I assist you today with our luxury collections or order status?',
            products: [],
            orders: [],
            quickActions: [
                { label: isRTL() ? '⌚ ساعات أوديمار بيغيه' : '⌚ Audemars Piguet Watches', action: 'PROMPT' },
                { label: isRTL() ? '👜 حقائب هيرميس الأصلية' : '👜 Hermès Bags', action: 'PROMPT' },
                { label: isRTL() ? '💎 مجوهرات كارتييه الملكية' : '💎 Cartier Jewelry', action: 'PROMPT' },
                { label: isRTL() ? '📦 تتبع شحنتي' : '📦 Track My Order', action: 'NAVIGATE', payload: '/orders' }
            ]
        }
    ]);
    const [aiTyping, setAiTyping] = useState(false);

    const stompClientRef = useRef(null);
    const messagesEndRef = useRef(null);
    
    // Media State
    const [isRecording, setIsRecording] = useState(false);
    const [recordingTime, setRecordingTime] = useState(0);
    const recordingTimerRef = useRef(null);
    const [showStickers, setShowStickers] = useState(false);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const fileInputRef = useRef(null);
    const chatInputRef = useRef(null);
    const isUploadingRef = useRef(false);
    const [isUploading, setIsUploading] = useState(false);
    const recordingIntervalRef = useRef(null);

    // AI Voice & Speech State
    const activeAiAudioRef = useRef(null);
    const [isPlayingAiVoiceId, setIsPlayingAiVoiceId] = useState(null);
    const [isAiVoiceAutoPlay, setIsAiVoiceAutoPlay] = useState(true);
    const [isAiListeningMic, setIsAiListeningMic] = useState(false);
    const aiSpeechRecognitionRef = useRef(null);

    // UI Extras
    const [previewUpload, setPreviewUpload] = useState(null);
    const [fullscreenMedia, setFullscreenMedia] = useState(null);
    const [isMobileFullscreen, setIsMobileFullscreen] = useState(false);
    const [replyToContext, setReplyToContext] = useState(null);
    const [isFabHidden, setIsFabHidden] = useState(false);
    const scrollTimeoutRef = useRef(null);
    const lastScrollYRef = useRef(0);

    // Auto-hide floating AI chat button on scroll/swipe so it never obstructs browsing
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || 0;
            const diff = Math.abs(currentScrollY - lastScrollYRef.current);

            if (diff > 2) {
                setIsFabHidden(true);
            }

            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            scrollTimeoutRef.current = setTimeout(() => {
                setIsFabHidden(false);
            }, 850);

            lastScrollYRef.current = currentScrollY;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        window.addEventListener('touchmove', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('touchmove', handleScroll);
        };
    }, []);

    // Lock body scroll when chat window is open on mobile
    useEffect(() => {
        if (isOpen) {
            document.body.classList.add('chat-window-open');
        } else {
            document.body.classList.remove('chat-window-open');
        }
        return () => {
            document.body.classList.remove('chat-window-open');
        };
    }, [isOpen]);

    // Mutable refs to prevent stale closure issues in popstate event listeners
    const isOpenRef = useRef(isOpen);
    const activeC2cSessionRef = useRef(activeC2cSession);

    useEffect(() => {
        isOpenRef.current = isOpen;
    }, [isOpen]);

    useEffect(() => {
        activeC2cSessionRef.current = activeC2cSession;
    }, [activeC2cSession]);

    // Maintain global chat level indicator and push history states for mobile device back button
    useEffect(() => {
        if (!isOpen) {
            window.__luxvono_chat_level = null;
            return;
        }

        if (activeC2cSession?.sessionId) {
            window.__luxvono_chat_level = 'thread';
            if (typeof window !== 'undefined' && window.history.state?.luxvonoChatLevel !== 'thread') {
                try {
                    window.history.pushState({ luxvonoChatLevel: 'thread', sessionId: activeC2cSession.sessionId }, '');
                } catch (_) {}
            }
        } else {
            window.__luxvono_chat_level = 'list';
            if (typeof window !== 'undefined' && !window.history.state?.luxvonoChatLevel) {
                try {
                    window.history.pushState({ luxvonoChatLevel: 'list' }, '');
                } catch (_) {}
            }
        }
    }, [isOpen, activeC2cSession?.sessionId]);

    // Handle mobile screen back button (Android back button, gesture navigation, and browser back)
    useEffect(() => {
        const handleChatPopState = () => {
            // Level 1: If user was in an active chat conversation, return to conversations list
            if (activeC2cSessionRef.current) {
                window.__luxvono_handling_chat_back = true;
                setTimeout(() => { window.__luxvono_handling_chat_back = false; }, 350);
                setActiveC2cSession(null);
                window.__luxvono_chat_level = 'list';
                return;
            }

            // Level 2: If user was in the conversations list, close chat and remain on store page
            if (isOpenRef.current) {
                window.__luxvono_handling_chat_back = true;
                setTimeout(() => { window.__luxvono_handling_chat_back = false; }, 350);
                setIsOpen(false);
                window.__luxvono_chat_level = null;
                return;
            }
        };

        window.addEventListener('popstate', handleChatPopState);
        return () => window.removeEventListener('popstate', handleChatPopState);
    }, []);

    const handleBackToConversations = useCallback(() => {
        setActiveC2cSession(null);
        window.__luxvono_chat_level = 'list';
        if (typeof window !== 'undefined' && window.history.state?.luxvonoChatLevel === 'thread') {
            window.history.back();
        }
    }, []);

    const STICKERS = ['👍', '❤️', '🌟', '😊', '🔥', '🎉', '👋', '🙏', '👑', '💎', '⌚'];

    const formatTime = (sec) => {
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
    };

    const customerId = useMemo(() => {
        if (isAuthenticated && user?.id) {
            const parsed = Number(user.id);
            if (Number.isFinite(parsed)) return parsed;
        }
        if (typeof window !== 'undefined') {
            let guestId = localStorage.getItem('luxvono_guest_chat_id');
            if (!guestId) {
                guestId = `-${Math.floor(100000 + Math.random() * 900000)}`;
                localStorage.setItem('luxvono_guest_chat_id', guestId);
            }
            return Number(guestId);
        }
        return -1001;
    }, [isAuthenticated, user]);

    const [isStompConnected, setIsStompConnected] = useState(false);

    const playFeedback = (type) => {
        triggerVibration(type);
        playSound(type, soundEnabled);
    };

    const closeChat = useCallback(() => {
        setIsOpen(false);
        setActiveC2cSession(null);
        setIsMobileFullscreen(false);
        window.__luxvono_chat_level = null;
        if (typeof window !== 'undefined' && window.history.state?.luxvonoChatLevel) {
            window.history.back();
        }
    }, []);

    const handleCopy = (id, text) => {
        if (typeof navigator !== 'undefined' && navigator.clipboard) {
            navigator.clipboard.writeText(text);
            setCopiedMsgId(id);
            setTimeout(() => setCopiedMsgId(null), 2000);
        }
    };

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    useEffect(() => {
        if (!isOpen) {
            document.body.classList.remove('chat-modal-open');
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            return;
        }

        document.body.classList.add('chat-modal-open');
        if (typeof window !== 'undefined') {
            window.history.pushState({ luxvonoModal: 'chat' }, '');
            if (window.innerWidth <= 768) {
                document.body.style.overflow = 'hidden';
                document.documentElement.style.overflow = 'hidden';
            }
        }

        const handlePopState = () => {
            setIsOpen(false);
            setIsMobileFullscreen(false);
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            document.body.classList.remove('chat-modal-open');
            document.body.style.overflow = '';
            document.documentElement.style.overflow = '';
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isOpen]);

    const loadSupportChat = useCallback(async (silent = false) => {
        if (!customerId) return;
        if (isSellerMode && sessionId) {
            try {
                const res = await getSessionChatMessages(sessionId);
                const msgs = res?.data || (Array.isArray(res) ? res : []);
                if (Array.isArray(msgs)) {
                    setSupportMessages(prev => {
                        const currentIds = new Set(prev.map(m => String(m.id || m.timestamp)));
                        const newIncoming = msgs.some(m => !currentIds.has(String(m.id || m.timestamp)) && Number(m.senderId) !== Number(customerId) && m.senderType !== 'user');
                        if (newIncoming && !silent) {
                            playFeedback('received');
                        }
                        return msgs;
                    });
                }
            } catch (err) {
                console.warn('loadSupportChat seller mode error:', err);
            }
            return;
        }

        try {
            const conv = await getSupportConversation(customerId);
            if (conv?.sessionId && !isSellerMode) {
                setSessionId(conv.sessionId);
            }
            if (Array.isArray(conv?.messages) && !isSellerMode) {
                setSupportMessages(prev => {
                    const currentIds = new Set(prev.map(m => String(m.id || m.timestamp)));
                    const newIncoming = conv.messages.some(m => !currentIds.has(String(m.id || m.timestamp)) && Number(m.senderId) !== Number(customerId) && m.senderType !== 'user');
                    if (newIncoming && !silent) {
                        playFeedback('received');
                    }
                    return conv.messages;
                });
            }
        } catch (err) {
            console.warn('loadSupportChat error:', err);
        }
    }, [customerId, isSellerMode, sessionId]);

    // Unified Direct Message & Seller Threads Handler
    const loadC2cThreads = useCallback(async (silent = false) => {
        if (!isAuthenticated) return;
        if (!silent) setC2cThreadsLoading(true);
        try {
            const [c2cRes, custRes] = await Promise.allSettled([
                getC2cChatThreads(),
                getCustomerChatThreads()
            ]);
            const c2cList = c2cRes.status === 'fulfilled'
                ? (c2cRes.value?.data || (Array.isArray(c2cRes.value) ? c2cRes.value : []))
                : [];
            const custList = custRes.status === 'fulfilled'
                ? (custRes.value?.data || (Array.isArray(custRes.value) ? custRes.value : []))
                : [];

            const merged = [];
            const seenSessions = new Set();
            const seenPeers = new Set();

            for (const item of (Array.isArray(c2cList) ? c2cList : [])) {
                const sid = Number(item.sessionId || item.id || 0);
                const pid = Number(item.peerUserId || item.recipientId || item.sellerId || 0);
                if (sid > 0 && seenSessions.has(sid)) continue;
                if (pid > 0 && seenPeers.has(pid)) continue;
                if (sid > 0) seenSessions.add(sid);
                if (pid > 0) seenPeers.add(pid);
                merged.push(item);
            }

            for (const item of (Array.isArray(custList) ? custList : [])) {
                if (item.sessionType === 'CUSTOMER_SUPPORT') continue;
                const sid = Number(item.sessionId || item.id || 0);
                const pid = Number(item.sellerId || item.peerUserId || item.recipientId || 0);
                if (sid > 0 && seenSessions.has(sid)) continue;
                if (pid > 0 && seenPeers.has(pid)) continue;
                if (sid > 0) seenSessions.add(sid);
                if (pid > 0) seenPeers.add(pid);
                merged.push({
                    ...item,
                    sessionId: sid,
                    peerUserId: pid,
                    recipientId: pid,
                    peerDisplayName: item.customerName || item.peerDisplayName || item.recipientName,
                    recipientName: item.customerName || item.peerDisplayName || item.recipientName,
                    peerAvatarUrl: item.customerAvatar || item.peerAvatarUrl || item.recipientAvatar,
                    recipientAvatar: item.customerAvatar || item.peerAvatarUrl || item.recipientAvatar,
                    isBlocked: Boolean(item.isBlocked || item.status === 'BLOCKED')
                });
            }

            setC2cThreads(merged);
        } catch (e) {
            console.warn('loadC2cThreads error:', e);
        } finally {
            if (!silent) setC2cThreadsLoading(false);
        }
    }, [isAuthenticated]);

    const loadC2cMessages = useCallback(async (sessId, silent = false) => {
        if (!sessId) return;
        try {
            const res = await getSessionChatMessages(sessId);
            const msgs = res?.data || (Array.isArray(res) ? res : []);
            if (Array.isArray(msgs)) {
                setC2cMessages(prev => {
                    const currentIds = new Set(prev.map(m => String(m.id || m.timestamp)));
                    const hasNew = msgs.some(m => !currentIds.has(String(m.id || m.timestamp)) && Number(m.senderId) !== Number(customerId));
                    if (hasNew && !silent) {
                        playFeedback('received');
                    }
                    const pending = prev.filter(m => String(m.id).startsWith('c2c-temp-'));
                    if (pending.length > 0) {
                        const existingTexts = new Set(msgs.map(m => (m.message || m.content || '').trim()));
                        const remainingPending = pending.filter(p => !existingTexts.has((p.message || p.content || '').trim()));
                        return [...msgs, ...remainingPending];
                    }
                    return msgs;
                });
            }
        } catch (e) {
            console.warn('loadC2cMessages error:', e);
        }
    }, [customerId]);

    const sendC2cMessage = async (text, optimisticId = null) => {
        if (!text || !text.trim() || !activeC2cSession) return;
        const trimmed = text.trim();
        const tempId = optimisticId || `c2c-temp-${Date.now()}`;
        const customerDisplayName = user ? (user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user.username || user.email || 'Customer')) : 'VIP Customer';

        const optimisticMsg = {
            id: tempId,
            sessionId: activeC2cSession.sessionId,
            senderId: customerId,
            receiverId: activeC2cSession.recipientUserId,
            senderType: 'customer',
            senderName: customerDisplayName,
            message: trimmed,
            content: trimmed,
            status: 'sending',
            timestamp: new Date().toISOString(),
            createdAt: new Date().toISOString()
        };
        setC2cMessages(prev => [...prev, optimisticMsg]);
        playFeedback('sent');
        scrollToBottom();

        try {
            const payload = {
                sessionId: activeC2cSession.sessionId,
                senderId: customerId,
                receiverId: activeC2cSession.recipientUserId,
                userId: customerId,
                senderType: 'customer',
                senderName: customerDisplayName,
                message: trimmed,
                content: trimmed
            };
            const saved = await sendChatMessage(payload);
            setC2cMessages(prev => prev.map(m => m.id === tempId ? { ...saved, status: 'sent' } : m));
            loadC2cThreads(true);
        } catch (err) {
            console.error('C2C send message failed:', err);
            setC2cMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: 'error' } : m));
            playFeedback('error');
        }
    };

    const handleShareProduct = (productData) => {
        const text = `[product] ${JSON.stringify(productData)}`;
        if (activeTab === C2C_TAB && activeC2cSession) {
            sendC2cMessage(text);
        } else {
            sendSupport(text);
        }
    };

    const fetchFriendship = useCallback(async (peerIdOverride = null) => {
        const peerId = peerIdOverride || activeC2cSession?.recipientUserId || activeC2cSession?.recipientId;
        if (!peerId) {
            setFriendshipStatus({ isFriend: false, canCall: false, status: 'NONE' });
            return;
        }
        try {
            setFriendshipLoading(true);
            const [status, blockStat] = await Promise.allSettled([
                getFriendshipStatus(peerId),
                customerId ? getChatBlockStatus(customerId, peerId) : Promise.resolve(null)
            ]);
            const resolvedFriend = status.status === 'fulfilled' && status.value
                ? status.value
                : { isFriend: false, canCall: false, status: 'NONE' };
            const resolvedBlock = blockStat.status === 'fulfilled' ? blockStat.value : null;
            const isBlockedNow = Boolean(
                resolvedFriend?.status === 'BLOCKED' ||
                resolvedBlock?.isBlocked ||
                resolvedBlock?.status === 'BLOCKED'
            );
            setFriendshipStatus({
                ...resolvedFriend,
                status: isBlockedNow ? 'BLOCKED' : (resolvedFriend?.status || 'NONE'),
                isBlocked: isBlockedNow,
                blockedByUserId: resolvedBlock?.blockedByUserId || resolvedFriend?.blockedByUserId || null
            });
            setActiveC2cSession(prev => prev && Number(prev.recipientUserId || prev.recipientId) === Number(peerId)
                ? { ...prev, isBlocked: isBlockedNow }
                : prev
            );
        } catch (e) {
            console.warn('Failed to get friendship status', e);
        } finally {
            setFriendshipLoading(false);
        }
    }, [activeC2cSession?.recipientUserId, activeC2cSession?.recipientId, customerId]);

    const handleBlockPeer = async () => {
        const peerId = activeC2cSession?.recipientUserId || activeC2cSession?.recipientId;
        if (!peerId) return;
        const confirmMsg = isRTL()
            ? 'هل أنت متأكد من حظر هذا الحساب؟ لن يتمكن من إرسال رسائل أو إجراء مكالمات معك حتى تقوم بفك الحظر.'
            : 'Are you sure you want to block this account? They will not be able to message or call you until you unblock them.';
        if (!window.confirm(confirmMsg)) return;
        try {
            setFriendshipLoading(true);
            await blockUser(peerId, 'User blocked in chat', customerId);
            setFriendshipStatus(prev => ({ ...prev, status: 'BLOCKED', isBlocked: true, isFriend: false, canCall: false, blockedByUserId: customerId }));
            setActiveC2cSession(prev => prev ? { ...prev, isBlocked: true } : prev);
            loadC2cThreads(true);
        } catch (e) {
            console.error('Block user error:', e);
        } finally {
            setFriendshipLoading(false);
        }
    };

    const handleUnblockPeer = async () => {
        const peerId = activeC2cSession?.recipientUserId || activeC2cSession?.recipientId;
        if (!peerId) return;
        try {
            setFriendshipLoading(true);
            await unblockUser(peerId, customerId);
            setFriendshipStatus({ isFriend: false, canCall: false, status: 'NONE', isBlocked: false, blockedByUserId: null });
            setActiveC2cSession(prev => prev ? { ...prev, isBlocked: false } : prev);
            loadC2cThreads(true);
            fetchFriendship(peerId);
        } catch (e) {
            console.error('Unblock user error:', e);
        } finally {
            setFriendshipLoading(false);
        }
    };

    useEffect(() => {
        if (activeTab === C2C_TAB && activeC2cSession) {
            fetchFriendship();
        } else {
            setFriendshipStatus({ isFriend: false, canCall: false, status: 'NONE' });
        }
    }, [activeTab, activeC2cSession?.sessionId, activeC2cSession?.recipientUserId, fetchFriendship]);

    const handleSendFriendRequest = async () => {
        const peerId = activeC2cSession?.recipientUserId || activeC2cSession?.recipientId;
        if (!peerId) return;
        try {
            setFriendshipLoading(true);
            const res = await sendFriendRequest(peerId);
            setFriendshipStatus(res);
            if (res?.isFriend) {
                alert(isRTL() ? 'أصبحتم أصدقاء الآن! تم فتح إمكانية الاتصال الصوتي والفيديو.' : 'You are now mutual friends! Audio and video calling is unlocked.');
            } else {
                alert(isRTL() ? 'تم إرسال طلب الصداقة بنجاح. ستتمكن من الاتصال بالعميل بمجرد قبول الطرف الآخر.' : 'Friend request sent successfully. You can call as soon as they accept.');
            }
        } catch (e) {
            alert(e?.response?.data?.message || e.message || (isRTL() ? 'فشل إرسال طلب الصداقة' : 'Failed to send friend request'));
        } finally {
            setFriendshipLoading(false);
        }
    };

    const handleAcceptFriendRequest = async () => {
        const peerId = activeC2cSession?.recipientUserId || activeC2cSession?.recipientId;
        if (!peerId) return;
        try {
            setFriendshipLoading(true);
            const res = await acceptFriendRequest(peerId);
            setFriendshipStatus(res);
            setShowFriendshipModal(false);
            alert(isRTL() ? 'تم قبول طلب الصداقة! تم تفعيل المكالمات الصوتية والمرئية الآن.' : 'Friend request accepted! Voice and video calling is now active.');
        } catch (e) {
            alert(e?.response?.data?.message || e.message || (isRTL() ? 'فشل قبول طلب الصداقة' : 'Failed to accept friend request'));
        } finally {
            setFriendshipLoading(false);
        }
    };

    const handleDeclineFriendRequest = async () => {
        const peerId = activeC2cSession?.recipientUserId || activeC2cSession?.recipientId;
        if (!peerId) return;
        try {
            setFriendshipLoading(true);
            const res = await declineFriendRequest(peerId);
            setFriendshipStatus(res);
            setShowFriendshipModal(false);
        } catch (e) {
            console.warn(e);
        } finally {
            setFriendshipLoading(false);
        }
    };

    // Initial load
    useEffect(() => {
        loadSupportChat(true);

        const handleOpenSellerChat = async (e) => {
            const data = e.detail;
            if (!data) return;
            setProductContext(data);
            setIsOpen(true);

            const rawSellerId = Number(data.sellerId || data.vendorId || 0);
            const targetProductId = data.productId ? Number(data.productId) : null;
            const isPlatformProduct = !rawSellerId || rawSellerId === 1 || rawSellerId === 5;

            if (isPlatformProduct) {
                // Platform-owned product -> Route to LUXVONO Technical Support (CUSTOMER_SUPPORT)
                setIsSellerMode(false);
                setActiveSellerInfo(null);
                setActiveTab(SUPPORT_TAB);
                try {
                    const session = await startSupportSession(customerId, null, null, targetProductId);
                    if (session?.id) {
                        setSessionId(session.id);
                        const msgs = await getSessionChatMessages(session.id);
                        const list = msgs?.data || (Array.isArray(msgs) ? msgs : []);
                        setSupportMessages(list);
                    }
                } catch (err) {
                    console.error('Failed to initialize platform support session:', err);
                }
            } else {
                // Seller-owned product -> Route strictly to unified conversation with Seller (NEVER Technical Support)
                setIsSellerMode(true);
                setActiveSellerInfo(data);
                setActiveTab(C2C_TAB);
                try {
                    const session = await startSupportSession(customerId, null, rawSellerId, targetProductId);
                    const resolvedSid = session?.id || session?.sessionId;
                    const resolvedPeerId = Number(session?.sellerId || session?.recipientId || rawSellerId);
                    if (resolvedSid) {
                        const newSessionObj = {
                            sessionId: resolvedSid,
                            recipientUserId: resolvedPeerId,
                            recipientId: resolvedPeerId,
                            recipientName: data.sellerName || (isRTL() ? 'صاحب المتجر المعتمد' : 'Verified Seller'),
                            recipientAvatar: data.sellerAvatar || null,
                            isSeller: true,
                            isBlocked: Boolean(session?.status === 'BLOCKED')
                        };
                        setActiveC2cSession(newSessionObj);
                        activeC2cSessionRef.current = newSessionObj;
                        window.__luxvono_chat_level = 'thread';
                        await loadC2cMessages(resolvedSid, false);
                        loadC2cThreads(true);
                    }
                } catch (err) {
                    console.error('Failed to initialize unified seller chat session:', err);
                }
            }
        };

        const handleOpenGeneralChat = async (e) => {
            const data = e?.detail;
            if (data?.product) setProductContext(data.product);
            setIsSellerMode(false);
            setActiveSellerInfo(null);
            setIsOpen(true);

            const targetTab = data?.tab || (isAuthenticated ? C2C_TAB : SUPPORT_TAB);
            setActiveTab(targetTab);

            if (targetTab === C2C_TAB) {
                if (data?.recipientUserId) {
                    try {
                        const sess = await getOrCreateC2cSession(data.recipientUserId);
                        if (sess?.id) {
                            const newSessionObj = {
                                sessionId: sess.id,
                                recipientUserId: data.recipientUserId,
                                recipientName: data.recipientName || 'Customer',
                                recipientAvatar: data.recipientAvatar,
                                recipientUsername: data.recipientUsername
                            };
                            setActiveC2cSession(newSessionObj);
                            activeC2cSessionRef.current = newSessionObj;
                            window.__luxvono_chat_level = 'thread';
                            loadC2cMessages(sess.id, false);
                        }
                    } catch (err) {
                        console.error('Failed to init C2C session from event:', err);
                    }
                }
                loadC2cThreads(false);
            } else {
                loadSupportChat(false);
            }
        };

        const handleStartSellerCall = (e) => {
            const data = e.detail;
            if (data) {
                setRemoteName(data.sellerName || data.remoteName || 'صاحب المتجر المعتمد');
                setCallType(data.callType || data.type || 'voice');
                setCallStatus('calling');
                setIsSellerMode(true);
                setIsOpen(true);
            }
        };

        window.addEventListener('open-seller-chat', handleOpenSellerChat);
        window.addEventListener('luxvono-chat:open', handleOpenGeneralChat);
        window.addEventListener('start-seller-call', handleStartSellerCall);
        return () => {
            window.removeEventListener('open-seller-chat', handleOpenSellerChat);
            window.removeEventListener('luxvono-chat:open', handleOpenGeneralChat);
            window.removeEventListener('start-seller-call', handleStartSellerCall);
        };
    }, [customerId, loadSupportChat, isAuthenticated, loadC2cThreads, loadC2cMessages]);

    // Active polling sync for Support (every 2.5s when open, every 8s in background when closed)
    useEffect(() => {
        if (activeTab === SUPPORT_TAB) {
            loadSupportChat(true);
            const intervalMs = isOpen ? 2500 : 8000;
            const pollInterval = setInterval(() => {
                loadSupportChat(true);
            }, intervalMs);
            return () => clearInterval(pollInterval);
        }
    }, [isOpen, activeTab, loadSupportChat]);

    // Active polling sync for C2C Conversations & Threads
    useEffect(() => {
        if (!isOpen || activeTab !== C2C_TAB) return;
        if (activeC2cSession?.sessionId) {
            loadC2cMessages(activeC2cSession.sessionId, true);
            const interval = setInterval(() => {
                loadC2cMessages(activeC2cSession.sessionId, true);
            }, 2500);
            return () => clearInterval(interval);
        } else {
            loadC2cThreads(true);
            const interval = setInterval(() => {
                loadC2cThreads(true);
            }, 6000);
            return () => clearInterval(interval);
        }
    }, [isOpen, activeTab, activeC2cSession?.sessionId, loadC2cMessages, loadC2cThreads]);

    // STOMP real-time listener for active C2C session
    useEffect(() => {
        const client = stompClientInstance || stompClientRef.current;
        if (!activeC2cSession?.sessionId || !client || (!client.connected && !client.active)) {
            return;
        }
        let sub = null;
        try {
            sub = client.subscribe(`/topic/chat/${activeC2cSession.sessionId}`, (msg) => {
                try {
                    const newMsg = JSON.parse(msg.body);
                    if (newMsg) {
                        setC2cMessages(prev => {
                            const newId = String(newMsg.id || '');
                            if (newId && prev.some(m => String(m.id) === newId)) return prev;
                            const tempIdx = prev.findIndex(m =>
                                String(m.id).startsWith('c2c-temp-') &&
                                (m.message || m.content || '').trim() === (newMsg.message || newMsg.content || '').trim()
                            );
                            if (Number(newMsg.senderId) !== Number(customerId)) {
                                playFeedback('received');
                            }
                            if (tempIdx !== -1) {
                                const copy = [...prev];
                                copy[tempIdx] = { ...newMsg, status: 'sent' };
                                return copy;
                            }
                            return [...prev, newMsg];
                        });
                    }
                } catch (e) {}
            });
        } catch (err) {}
        return () => {
            if (sub) {
                try { sub.unsubscribe(); } catch (_) {}
            }
        };
    }, [activeC2cSession?.sessionId, isStompConnected, customerId]);

    // Persistent WebSocket / STOMP Real-time Connection
    useEffect(() => {
        const wsUrl = `${API_BASE || ''}/ws/chat`;
        const client = new Client({
            webSocketFactory: () => new SockJS(wsUrl),
            reconnectDelay: 3000,
            heartbeatIncoming: 4000,
            heartbeatOutgoing: 4000,
            onConnect: () => {
                window.__LUXVONO_STOMP_CLIENT = client;
                setIsStompConnected(true);
                setStompClientInstance(client);
            },
            onDisconnect: () => {
                window.__LUXVONO_STOMP_CLIENT = null;
                setIsStompConnected(false);
            },
            onStompError: (frame) => {
                console.warn('STOMP broker error:', frame?.headers?.['message']);
            }
        });

        client.activate();
        stompClientRef.current = client;

        return () => {
            if (stompClientRef.current) {
                try { stompClientRef.current.deactivate(); } catch (_) {}
            }
            setIsStompConnected(false);
        };
    }, []);

    // Subscribe to active session chat topic for instantaneous live messages
    useEffect(() => {
        const client = stompClientInstance || stompClientRef.current;
        if (!sessionId || !client || (!client.connected && !client.active)) {
            return;
        }

        let sub = null;
        let subCust = null;
        const handleIncoming = (msg) => {
            try {
                const newMsg = JSON.parse(msg.body);
                if (newMsg) {
                    setSupportMessages(prev => {
                        const newId = String(newMsg.id || '');
                        const newContent = (newMsg.message || newMsg.content || '').trim();

                        // 1. If exact ID already exists in messages, don't duplicate
                        if (newId && prev.some(m => String(m.id) === newId)) {
                            return prev;
                        }

                        // 2. Check if this resolves an optimistic pending message
                        const tempIdx = prev.findIndex(m =>
                            String(m.id).startsWith('temp-') &&
                            (m.message || m.content || '').trim() === newContent &&
                            Number(m.senderId) === Number(newMsg.senderId)
                        );

                        const isTheirs = Number(newMsg.senderId) !== Number(customerId) && newMsg.senderType !== 'user';
                        if (isTheirs) {
                            playFeedback('received');
                        }

                        if (tempIdx !== -1) {
                            const copy = [...prev];
                            copy[tempIdx] = { ...newMsg, status: 'sent' };
                            return copy;
                        }

                        // 3. Prevent duplicate text/voice arriving within a small time window
                        const isDuplicate = prev.some(m =>
                            !String(m.id).startsWith('temp-') &&
                            (m.message || m.content || '').trim() === newContent &&
                            Number(m.senderId) === Number(newMsg.senderId) &&
                            Math.abs(new Date(m.timestamp || Date.now()) - new Date(newMsg.timestamp || Date.now())) < 4000
                        );
                        if (isDuplicate) {
                            return prev;
                        }

                        return [...prev, newMsg];
                    });
                    scrollToBottom();
                }
            } catch (e) {
                console.error("Failed to parse incoming chat message:", e);
            }
        };

        let subFriend = null;
        try {
            sub = client.subscribe(`/topic/chat/${sessionId}`, handleIncoming);
            if (customerId) {
                subCust = client.subscribe(`/topic/customer/chat/${customerId}`, handleIncoming);
                subFriend = client.subscribe(`/topic/customer/${customerId}/friendship`, () => {
                    fetchFriendship();
                });
            }
        } catch (err) {
            console.warn('Chat subscription error:', err);
        }

        return () => {
            if (sub) {
                try { sub.unsubscribe(); } catch (_) {}
            }
            if (subCust) {
                try { subCust.unsubscribe(); } catch (_) {}
            }
            if (subFriend) {
                try { subFriend.unsubscribe(); } catch (_) {}
            }
        };
    }, [sessionId, isStompConnected, stompClientInstance, customerId, fetchFriendship]);

    useEffect(() => {
        scrollToBottom();
    }, [supportMessages, aiMessages, supportTyping, aiTyping, activeTab]);

    const stopAiVoice = () => {
        if (activeAiAudioRef.current) {
            try {
                activeAiAudioRef.current.pause();
                activeAiAudioRef.current.currentTime = 0;
            } catch (e) {}
            activeAiAudioRef.current = null;
        }
        if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
            try {
                window.speechSynthesis.cancel();
            } catch (e) {}
        }
        setIsPlayingAiVoiceId(null);
    };

    const speakAiText = async (text, msgId = null) => {
        if (!text || !text.trim()) return;
        if (isPlayingAiVoiceId === msgId && msgId !== null) {
            stopAiVoice();
            return;
        }
        stopAiVoice();

        const cleanText = text.replace(/[*_#`]/g, '').trim();
        const currentLang = getCurrentLanguage();
        const voiceLang = currentLang.startsWith('ar') ? 'ar' : (currentLang.startsWith('so') ? 'ar' : 'en');
        const voicePersona = 'ar-SA-HamedNeural';

        setIsPlayingAiVoiceId(msgId || 'current');

        try {
            const ttsUrl = `/api/v1/admin/ai/tts?text=${encodeURIComponent(cleanText)}&lang=${voiceLang}&voice=${voicePersona}`;
            const res = await fetch(ttsUrl);
            if (res.ok) {
                const blob = await res.blob();
                if (blob && blob.size > 100) {
                    const blobUrl = URL.createObjectURL(blob);
                    const audio = new Audio(blobUrl);
                    activeAiAudioRef.current = audio;

                    audio.onended = () => {
                        setIsPlayingAiVoiceId(null);
                        activeAiAudioRef.current = null;
                        URL.revokeObjectURL(blobUrl);
                    };
                    audio.onerror = () => {
                        setIsPlayingAiVoiceId(null);
                        activeAiAudioRef.current = null;
                        URL.revokeObjectURL(blobUrl);
                        playBrowserSpeech(cleanText, currentLang);
                    };

                    await audio.play();
                    return;
                }
            }
        } catch (e) {
            console.warn('Backend TTS fetch failed, falling back to browser speech synthesis', e);
        }

        playBrowserSpeech(cleanText, currentLang);
    };

    const playBrowserSpeech = (cleanText, langCode) => {
        if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
            setIsPlayingAiVoiceId(null);
            return;
        }
        try {
            window.speechSynthesis.cancel();
            const utterance = new SpeechSynthesisUtterance(cleanText);
            utterance.lang = langCode === 'so' ? 'ar-SA' : (langCode.startsWith('ar') ? 'ar-SA' : (langCode.startsWith('fr') ? 'fr-FR' : (langCode.startsWith('de') ? 'de-DE' : 'en-US')));
            utterance.rate = 0.95;
            utterance.pitch = 0.95;
            utterance.onend = () => setIsPlayingAiVoiceId(null);
            utterance.onerror = () => setIsPlayingAiVoiceId(null);
            window.speechSynthesis.speak(utterance);
        } catch (e) {
            setIsPlayingAiVoiceId(null);
        }
    };

    const toggleAiSpeechRecognition = () => {
        if (typeof window === 'undefined') return;
        const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!SpeechRecognition) {
            alert(isRTL() ? 'المتصفح لا يدعم التعرف على الصوت المباشر.' : 'Speech recognition is not supported in this browser.');
            return;
        }

        if (isAiListeningMic) {
            if (aiSpeechRecognitionRef.current) {
                try { aiSpeechRecognitionRef.current.stop(); } catch (e) {}
            }
            setIsAiListeningMic(false);
            return;
        }

        try {
            const recognition = new SpeechRecognition();
            const curLang = getCurrentLanguage();
            recognition.lang = curLang.startsWith('ar') ? 'ar-SA' : (curLang.startsWith('so') ? 'so-SO' : (curLang.startsWith('fr') ? 'fr-FR' : (curLang.startsWith('de') ? 'de-DE' : 'en-US')));
            recognition.continuous = false;
            recognition.interimResults = true;

            recognition.onstart = () => {
                setIsAiListeningMic(true);
                playFeedback('click');
            };

            recognition.onresult = (event) => {
                const transcript = Array.from(event.results)
                    .map(result => result[0])
                    .map(result => result.transcript)
                    .join('');
                setInput(transcript);
            };

            recognition.onerror = (event) => {
                console.warn('Speech recognition error:', event.error);
                setIsAiListeningMic(false);
            };

            recognition.onend = () => {
                setIsAiListeningMic(false);
            };

            aiSpeechRecognitionRef.current = recognition;
            recognition.start();
        } catch (err) {
            console.error('Failed to start speech recognition:', err);
            setIsAiListeningMic(false);
        }
    };

    const sendAi = async (text, customImageUrl = null) => {
        if (!text || !text.trim()) return;
        const queryText = text.trim();
        const userMessage = {
            id: `ai-user-${Date.now()}`,
            role: 'user',
            text: queryText,
            imageUrl: customImageUrl,
            products: [],
            orders: []
        };
        setAiMessages((prev) => [...prev, userMessage]);
        setInput('');
        setAiTyping(true);
        playFeedback('sent');

        try {
            const historyPayload = aiMessages.slice(-6).map(m => ({ role: m.role, content: m.text }));
            const currentLang = getCurrentLanguage();
            const response = await askAiAssistant(queryText, customerId, historyPayload, currentLang);
            const aiReplyText = response?.reply || t('COULD_NOT_PROCESS');
            const aiMessage = {
                id: `ai-reply-${Date.now()}`,
                role: 'assistant',
                text: aiReplyText,
                products: Array.isArray(response?.products) ? response.products : [],
                orders: Array.isArray(response?.orders) ? response.orders : [],
                quickActions: Array.isArray(response?.quickActions) ? response.quickActions : [],
                requiresLogin: !!response?.requiresLogin,
                escalatedToHuman: !!response?.escalatedToHuman
            };
            setAiMessages((prev) => [...prev, aiMessage]);
            playFeedback('received');

            if (soundEnabled && isAiVoiceAutoPlay) {
                speakAiText(aiReplyText, aiMessage.id);
            }
        } catch (err) {
            setAiMessages((prev) => [...prev, {
                id: `ai-error-${Date.now()}`,
                role: 'assistant',
                text: isRTL() 
                    ? 'عذراً، حدث خطأ مؤقت في معالجة طلبك. يرجى المحاولة مرة أخرى أو التواصل مع كونسيرج VIP.'
                    : 'We apologize, a temporary error occurred. Please try again or switch to VIP Concierge.',
                products: [],
                orders: [],
                quickActions: [
                    { label: isRTL() ? '🎧 التحويل لكونسيرج VIP' : '🎧 Switch to VIP Concierge', action: 'TALK_TO_HUMAN' }
                ]
            }]);
            console.error('AI assistant request failed:', err);
            playFeedback('error');
        } finally {
            setAiTyping(false);
        }
    };

    const handleSend = (e) => {
        e.preventDefault();
        if (!input.trim()) return;
        const textToSend = input;
        setInput('');
        if (activeTab === C2C_TAB) {
            if (activeC2cSession) {
                sendC2cMessage(textToSend);
            }
        } else if (activeTab === SUPPORT_TAB) {
            sendSupport(textToSend);
        } else {
            sendAi(textToSend);
        }
        // Keep mobile virtual keyboard open across sends
        if (chatInputRef.current) {
            chatInputRef.current.focus({ preventScroll: true });
        }
        setTimeout(() => {
            chatInputRef.current?.focus({ preventScroll: true });
        }, 40);
    };

    const sendSupport = async (text, optimisticId = null) => {
        if (!text || !text.trim()) return;
        const trimmed = text.trim();
        const tempId = optimisticId || `temp-${Date.now()}`;
        const optimisticMsg = {
            id: tempId,
            senderId: customerId,
            senderType: 'user',
            message: trimmed,
            content: trimmed,
            status: 'sending',
            timestamp: new Date().toISOString()
        };
        setSupportMessages(prev => [...prev, optimisticMsg]);
        playFeedback('sent');
        scrollToBottom();

        // Deliver via REST API with optimistic update (server broadcasts via STOMP /topic/chat/{sessionId})
        const customerDisplayName = user ? (user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user.username || user.email || 'Customer')) : 'VIP Customer';
        try {
            const targetSellerId = isSellerMode ? Number(productContext?.sellerId || productContext?.vendorId || 1) : null;
            let currentSessionId = sessionId;

            if (!currentSessionId && isSellerMode && targetSellerId) {
                try {
                    const sess = await startSupportSession(customerId, null, targetSellerId, productContext?.productId ? Number(productContext.productId) : null);
                    if (sess?.id) {
                        currentSessionId = sess.id;
                        setSessionId(sess.id);
                    }
                } catch (e) {
                    console.warn('Auto-session init failed:', e);
                }
            }

            const payload = {
                userId: customerId,
                senderType: 'user',
                senderName: customerDisplayName,
                message: trimmed,
                content: trimmed,
                sessionId: currentSessionId,
                senderId: customerId,
                receiverId: targetSellerId,
                relatedProductId: productContext?.productId ? Number(productContext.productId) : null
            };

            const saved = await sendSupportMessage(payload);
            if (saved?.sessionId && !sessionId) {
                setSessionId(saved.sessionId);
            }
            setSupportMessages(prev => prev.map(m => m.id === tempId ? { ...saved, status: 'sent' } : m));
        } catch (err) {
            console.error('Support message REST delivery error:', err);
            setSupportMessages(prev => prev.map(m => m.id === tempId ? { ...m, status: 'error' } : m));
            playFeedback('error');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setIsUploading(true);
        try {
            const fileUrl = await uploadChatMedia(file);
            if (fileUrl) {
                const text = `[image] ${fileUrl}`;
                if (activeTab === C2C_TAB && activeC2cSession) {
                    await sendC2cMessage(text);
                } else {
                    await sendSupport(text);
                }
            }
        } catch (err) {
            console.error('File upload failed', err);
            alert(isRTL() ? 'فشل رفع الملف، يرجى المحاولة مجدداً.' : 'File upload failed, please try again.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const startRecording = async () => {
        if (isRecording) return;
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            const mimeType = getSupportedMimeType();
            const options = mimeType ? { mimeType } : undefined;
            const mediaRecorder = new MediaRecorder(stream, options);
            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = e => {
                if (e.data && e.data.size > 0) {
                    audioChunksRef.current.push(e.data);
                }
            };

            mediaRecorder.onstop = async () => {
                stream.getTracks().forEach(track => {
                    try { track.stop(); } catch (_) {}
                });
                
                if (audioChunksRef.current.length === 0) {
                    console.warn('No audio chunks captured');
                    return;
                }
                
                const finalType = mimeType || mediaRecorder.mimeType || 'audio/webm';
                const audioBlob = new Blob(audioChunksRef.current, { type: finalType });
                audioChunksRef.current = [];
                
                setIsUploading(true);
                try {
                    let ext = 'webm';
                    if (finalType.includes('mp4')) ext = 'm4a';
                    else if (finalType.includes('ogg')) ext = 'ogg';
                    else if (finalType.includes('aac')) ext = 'aac';
                    else if (finalType.includes('wav')) ext = 'wav';
                    else if (finalType.includes('mp3') || finalType.includes('mpeg')) ext = 'mp3';
                    else ext = 'webm';

                    const audioFile = new File([audioBlob], `voice-note-${Date.now()}.${ext}`, { type: finalType });
                    const fileUrl = await uploadChatMedia(audioFile);
                    if (fileUrl) {
                        const text = `[audio] ${fileUrl}`;
                        if (activeTab === C2C_TAB && activeC2cSession) {
                            await sendC2cMessage(text);
                        } else {
                            await sendSupport(text);
                        }
                    }
                } catch (err) {
                    console.error('Voice upload failed', err);
                    alert(isRTL() ? 'فشل إرسال التسجيل الصوتي.' : 'Voice note upload failed.');
                } finally {
                    setIsUploading(false);
                }
            };

            mediaRecorder.start(250);
            setIsRecording(true);
            setRecordingTime(0);
            if (recordingIntervalRef.current) clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = setInterval(() => {
                setRecordingTime(prev => prev + 1);
            }, 1000);
        } catch (err) {
            console.error('Microphone access error:', err);
            alert(isRTL() ? 'يرجى السماح بالوصول للميكروفون لتسجيل الصوت.' : 'Please allow microphone access to record audio.');
        }
    };

    const stopRecording = (shouldDiscard = false) => {
        if (recordingIntervalRef.current) {
            clearInterval(recordingIntervalRef.current);
            recordingIntervalRef.current = null;
        }
        setIsRecording(false);
        setRecordingTime(0);
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            if (shouldDiscard) {
                audioChunksRef.current = [];
            } else {
                try {
                    mediaRecorderRef.current.requestData();
                } catch (_) {}
            }
            try {
                mediaRecorderRef.current.stop();
            } catch (_) {}
        }
    };

    const sendSticker = (stickerUrl) => {
        const text = `[STICKER: ${stickerUrl}]`;
        if (activeTab === C2C_TAB && activeC2cSession) {
            sendC2cMessage(text);
        } else {
            sendSupport(text);
        }
        setShowStickers(false);
    };

    const renderSupportBody = () => {
        return (
            <>
                {!isAuthenticated && (
                    <div style={{
                        padding: '10px 14px',
                        borderRadius: '12px',
                        background: 'rgba(254, 44, 85, 0.06)',
                        border: '1px solid rgba(254, 44, 85, 0.2)',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--text)' }}>
                            <Headphones size={14} color="#FE2C55" />
                            <span>{isRTL() ? 'محادثة مباشرة مع الدعم الفني لـ LUXVONO' : 'Direct LUXVONO Live Support Chat'}</span>
                        </div>
                        <button type="button" onClick={() => navigate('/login')} style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#FE2C55',
                            fontSize: '11px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            textDecoration: 'underline'
                        }}>
                            {isRTL() ? 'تسجيل الدخول' : 'Sign In'}
                        </button>
                    </div>
                )}

                {supportMessages.length === 0 && (
                    <div className="chat-support-welcome-card" style={{
                        background: 'linear-gradient(135deg, rgba(20, 20, 25, 0.95) 0%, rgba(10, 10, 15, 0.98) 100%)',
                        border: '1px solid rgba(254, 44, 85, 0.25)',
                        borderRadius: '20px',
                        padding: '20px 16px',
                        textAlign: 'center',
                        margin: '10px 0',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
                    }}>
                        <div style={{
                            width: '54px',
                            height: '54px',
                            borderRadius: '50%',
                            background: '#000000',
                            border: '2px solid #FE2C55',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 0 20px rgba(254, 44, 85, 0.35)',
                            marginBottom: '12px'
                        }}>
                            <Headphones size={26} color="#FE2C55" />
                        </div>
                        <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: 800, color: '#ffffff' }}>
                            {isRTL() ? 'الدعم الفني وخدمة العملاء المباشرة' : 'Direct Live Technical Support'}
                        </h3>
                        <p style={{ margin: '0 0 16px 0', fontSize: '12.5px', color: '#999999', lineHeight: 1.6 }}>
                            {isRTL() 
                                ? 'أهلاً بك في LUXVONO. أنت الآن على تواصل مباشر مع فريق الدعم الفني وخدمة العملاء الحقيقي. موظفونا متواجدون لخدمتك ومساعدتك في كافة استفساراتك.'
                                : 'Welcome to LUXVONO. You are connected directly with our live customer support team. How can we help you today?'
                            }
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {[
                                { text: isRTL() ? '📦 استفسار عن حالة طلبية' : '📦 Track or inquire about an order', prompt: isRTL() ? 'أود الاستفسار عن حالة طلبيتي' : 'I would like to inquire about my order status' },
                                { text: isRTL() ? '💳 مساعدة في الدفع والفواتير' : '💳 Payment & checkout inquiry', prompt: isRTL() ? 'أحتاج مساعدة بخصوص الدفع والفاتورة' : 'I need help with payment and billing' },
                                { text: isRTL() ? '🔄 استرجاع أو استبدال منتج' : '🔄 Returns & Exchanges', prompt: isRTL() ? 'أرغب بالاستفسار عن سياسة الاستبدال والاسترجاع' : 'I want to ask about returns and exchange' },
                                { text: isRTL() ? '🛍️ الاستفسار عن توفر منتج أو مقاس' : '🛍️ Product availability & sizing', prompt: isRTL() ? 'أود الاستفسار عن توفر منتج ومواصفاته' : 'I would like to ask about product availability' }
                            ].map((chip, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => sendSupport(chip.prompt)}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.04)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '12px',
                                        padding: '9px 12px',
                                        color: '#ffffff',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        textAlign: isRTL() ? 'right' : 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        transition: 'all 0.2s ease'
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#FE2C55'; e.currentTarget.style.background = 'rgba(254, 44, 85, 0.08)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)'; e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; }}
                                >
                                    <span>{chip.text}</span>
                                    <Send size={13} color="#FE2C55" style={{ transform: isRTL() ? 'rotate(180deg)' : 'none' }} />
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {/* WhatsApp Standard Date Pill & E2E Encryption Banner */}
                <div className="whatsapp-date-pill">
                    {isRTL() ? 'اليوم' : 'Today'}
                </div>

                <div className="whatsapp-e2e-pill">
                    <span>🔒</span>
                    <span>{isRTL() ? 'الرسائل والمكالمات مشفرة تماماً بينك وبين موظف الدعم أو التاجر لحماية خصوصيتك.' : 'Messages and calls are end-to-end encrypted.'}</span>
                </div>

                {supportMessages.map((msg) => {
                    const mine = Number(msg.senderId) === Number(customerId) || msg.senderType === 'user' || msg.senderType === 'customer';
                    const text = msg.message || msg.content || '';
                    const timeStr = msg.createdAt || msg.timestamp 
                        ? new Date(msg.createdAt || msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) 
                        : '';
                    
                    const contentNode = renderUniversalChatContent(text, mine);

                    return (
                        <div key={msg.id || `${msg.timestamp}-${text}`} className={`chat-bubble ${mine ? 'mine' : 'theirs'}`}>
                            {!mine && (
                                <div className="chat-bubble-header">
                                    <span className="ai-bubble-tag" style={{ color: '#FE2C55' }}>
                                        <Headphones size={12} color="#FE2C55" /> {msg.senderName || (isRTL() ? 'الدعم الفني المباشر' : 'Live Support Specialist')}
                                    </span>
                                </div>
                            )}
                            {contentNode}
                            
                            {/* Timestamp & Read Receipt */}
                            {timeStr && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, fontSize: '10px', color: mine ? '#667781' : '#94a3b8', marginTop: 3, direction: 'ltr' }}>
                                    <span>{timeStr}</span>
                                    {mine && <CheckCheck size={13} color="#53bdeb" />}
                                </div>
                            )}
                        </div>
                    );
                })}
                {supportTyping && (
                    <div className="chat-typing">
                        <span className="typing-dots"><span></span><span></span><span></span></span>
                        <span>{isRTL() ? 'موظف الدعم يكتب...' : 'Support specialist is typing...'}</span>
                    </div>
                )}
            </>
        );
    };

    const renderC2cThreadsList = () => {
        const filteredThreads = (c2cThreads || []).filter(t => {
            if (!c2cSearchFilter.trim()) return true;
            const name = (t.peerDisplayName || t.recipientName || t.peerUsername || t.recipientUsername || '').toLowerCase();
            return name.includes(c2cSearchFilter.trim().toLowerCase());
        });

        return (
            <div className="c2c-threads-wrap">
                {/* Search Bar & New Chat CTA */}
                <div className="c2c-threads-search-bar">
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        background: '#1F2C34',
                        borderRadius: '10px',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        padding: '0 10px',
                        gap: '6px'
                    }}>
                        <Search size={15} color="#D4AF37" />
                        <input
                            type="text"
                            value={c2cSearchFilter}
                            onChange={(e) => setC2cSearchFilter(e.target.value)}
                            placeholder={isRTL() ? 'تصفية المحادثات بالاسم...' : 'Filter conversations...'}
                            style={{
                                width: '100%',
                                background: 'transparent',
                                border: 'none',
                                outline: 'none',
                                color: '#FFFFFF',
                                fontSize: '12px',
                                padding: '8px 0',
                                direction: isRTL() ? 'rtl' : 'ltr'
                            }}
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowCustomerSearchModal(true)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                            border: 'none',
                            color: '#000000',
                            fontSize: '11.5px',
                            fontWeight: '800',
                            padding: '8px 12px',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            boxShadow: '0 2px 8px rgba(212, 175, 55, 0.3)'
                        }}
                    >
                        <Plus size={15} strokeWidth={2.5} />
                        <span>{isRTL() ? 'محادثة جديدة' : 'New Chat'}</span>
                    </button>
                </div>

                {/* Threads Content */}
                {c2cThreadsLoading && c2cThreads.length === 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 0', gap: '8px' }}>
                        <div style={{ width: '24px', height: '24px', border: '2px solid #D4AF37', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                        <span style={{ fontSize: '12px', color: '#94A3B8' }}>{isRTL() ? 'جاري تحميل المحادثات...' : 'Loading conversations...'}</span>
                    </div>
                ) : filteredThreads.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px 16px', color: '#94A3B8' }}>
                        <div style={{
                            width: '54px',
                            height: '54px',
                            borderRadius: '50%',
                            background: '#16222b',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            marginBottom: '12px',
                            border: '1px solid rgba(212, 175, 55, 0.3)'
                        }}>
                            <Users size={26} color="#D4AF37" />
                        </div>
                        <h4 style={{ margin: '0 0 6px 0', color: '#FFFFFF', fontSize: '14px', fontWeight: '700' }}>
                            {isRTL() ? 'لا توجد محادثات سابقة' : 'No conversations yet'}
                        </h4>
                        <p style={{ margin: '0 0 16px 0', fontSize: '12px', color: '#94A3B8', lineHeight: 1.5 }}>
                            {isRTL() 
                                ? 'ابدأ أول محادثة خاصة مع عملاء LUXVONO وشاركهم أرقى المنتجات والمقتنيات.'
                                : 'Start your first direct chat with LUXVONO customers and share exclusive products.'}
                        </p>
                        <button
                            type="button"
                            onClick={() => setShowCustomerSearchModal(true)}
                            style={{
                                background: '#D4AF37',
                                border: 'none',
                                color: '#000000',
                                fontWeight: '800',
                                fontSize: '12px',
                                padding: '8px 18px',
                                borderRadius: '10px',
                                cursor: 'pointer',
                                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.3)'
                            }}
                        >
                            {isRTL() ? 'البحث عن عملاء والبدء 💬' : 'Find Customers & Chat 💬'}
                        </button>
                    </div>
                ) : (
                    filteredThreads.map((thread) => {
                        const recipientId = thread.peerUserId || thread.recipientId;
                        const recipientName = thread.peerDisplayName || thread.recipientName || thread.peerUsername || thread.recipientUsername || 'Customer';
                        const recipientAvatar = thread.peerAvatarUrl || thread.recipientAvatar;
                        const recipientUsername = thread.peerUsername || thread.recipientUsername;

                        let lastMsgText = thread.lastMessage || '';
                        if (lastMsgText.includes('[product]')) {
                            const parsedP = parseProductMessage(lastMsgText);
                            lastMsgText = parsedP?.name ? `🛍️ ${parsedP.name}` : (isRTL() ? '🛍️ شارك منتجاً فاخراً' : '🛍️ Shared a luxury product');
                        } else if (lastMsgText.includes('[IMAGE:') || lastMsgText.includes('[IMAGE: ')) {
                            lastMsgText = isRTL() ? '📷 صورة' : '📷 Photo';
                        } else if (lastMsgText.includes('[VOICE:') || lastMsgText.includes('[AUDIO:')) {
                            lastMsgText = isRTL() ? '🎵 رسالة صوتية' : '🎵 Voice Note';
                        } else if (lastMsgText.includes('[STICKER:')) {
                            lastMsgText = isRTL() ? '✨ ملصق' : '✨ Sticker';
                        }

                        const timeStr = thread.timestamp 
                            ? new Date(thread.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) 
                            : '';

                        return (
                            <div
                                key={thread.sessionId || thread.peerUserId || recipientId}
                                className="c2c-thread-item"
                                onClick={() => {
                                    const newSessionObj = {
                                        sessionId: thread.sessionId,
                                        recipientUserId: recipientId,
                                        recipientName: recipientName,
                                        recipientAvatar: recipientAvatar,
                                        recipientUsername: recipientUsername
                                    };
                                    setActiveC2cSession(newSessionObj);
                                    activeC2cSessionRef.current = newSessionObj;
                                    window.__luxvono_chat_level = 'thread';
                                    setC2cMessages([]);
                                    loadC2cMessages(thread.sessionId, false);
                                }}
                            >
                                <div className="c2c-thread-avatar">
                                    {recipientAvatar ? (
                                        <img src={recipientAvatar} alt="" />
                                    ) : (
                                        recipientName.charAt(0).toUpperCase()
                                    )}
                                    <span className="c2c-thread-online-dot" />
                                </div>

                                <div className="c2c-thread-content">
                                    <div className="c2c-thread-top">
                                        <span className="c2c-thread-name">{recipientName}</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span className="c2c-thread-time">{timeStr}</span>
                                            <button
                                                type="button"
                                                className="c2c-thread-delete-btn"
                                                title={isRTL() ? 'حذف المحادثة' : 'Delete conversation'}
                                                onClick={async (e) => {
                                                    e.stopPropagation();
                                                    const confirmMsg = isRTL() 
                                                        ? 'هل أنت متأكد من رغبتك في حذف هذه المحادثة بالكامل؟' 
                                                        : 'Are you sure you want to delete this conversation?';
                                                    if (!window.confirm(confirmMsg)) return;

                                                    try {
                                                        await deleteC2cChatThread(thread.sessionId);
                                                        setC2cThreads(prev => prev.filter(t => t.sessionId !== thread.sessionId));
                                                        if (activeC2cSession?.sessionId === thread.sessionId) {
                                                            setActiveC2cSession(null);
                                                        }
                                                    } catch (err) {
                                                        console.error('Failed to delete thread', err);
                                                        alert(isRTL() ? 'فشل حذف المحادثة' : 'Failed to delete conversation');
                                                    }
                                                }}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#94A3B8',
                                                    cursor: 'pointer',
                                                    padding: '2px 4px',
                                                    borderRadius: '4px',
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="c2c-thread-bottom">
                                        <span className="c2c-thread-last-msg">
                                            {lastMsgText || (isRTL() ? 'انقر لفتح المحادثة...' : 'Tap to open chat...')}
                                        </span>
                                        {thread.unreadCount > 0 && (
                                            <span className="c2c-thread-unread-pill">
                                                {thread.unreadCount}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        );
    };

    const renderC2cConversation = () => {
        return (
            <>
                <div className="whatsapp-date-pill">
                    {isRTL() ? 'اليوم' : 'Today'}
                </div>

                <div className="whatsapp-e2e-pill">
                    <span>🔒</span>
                    <span>{isRTL() ? 'المحادثة مشفرة تماماً بينك وبين العميل لحماية خصوصيتكما.' : 'End-to-end encrypted direct conversation.'}</span>
                </div>

                {/* Mutual Friendship & Calling Status Bar */}
                {friendshipStatus.status === 'ACCEPTED' ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        margin: '6px auto 10px',
                        padding: '4px 14px',
                        borderRadius: '20px',
                        backgroundColor: 'rgba(16, 185, 129, 0.12)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        fontSize: '11px',
                        color: '#34d399',
                        width: 'fit-content'
                    }}>
                        <UserCheck size={13} />
                        <span>{isRTL() ? 'أصدقاء مشتركون • الاتصال الصوتي والفيديو متاح' : 'Mutual Friends • Audio & Video Calling Active'}</span>
                    </div>
                ) : friendshipStatus.status === 'PENDING_RECEIVED' ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        margin: '8px 12px 12px',
                        padding: '10px 14px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(212, 175, 55, 0.14)',
                        border: '1px solid rgba(212, 175, 55, 0.35)',
                        fontSize: '12px',
                        color: '#fbbf24'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <UserPlus size={16} />
                            <div>
                                <div style={{ fontWeight: 600 }}>{isRTL() ? 'أرسل لك هذا العميل طلب صداقة' : 'Friend Request Received'}</div>
                                <div style={{ fontSize: '10.5px', color: '#cbd5e1', opacity: 0.85 }}>{isRTL() ? 'قبول الطلب يمكنكما من الاتصال الصوتي والفيديو' : 'Accepting unlocks audio & video calls'}</div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                            <button
                                onClick={handleAcceptFriendRequest}
                                disabled={friendshipLoading}
                                style={{
                                    background: 'linear-gradient(135deg, #d4af37, #b89327)',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '5px 12px',
                                    fontSize: '11.5px',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                }}
                            >
                                {isRTL() ? 'قبول' : 'Accept'}
                            </button>
                            <button
                                onClick={handleDeclineFriendRequest}
                                disabled={friendshipLoading}
                                style={{
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    color: '#cbd5e1',
                                    border: 'none',
                                    borderRadius: '6px',
                                    padding: '5px 10px',
                                    fontSize: '11.5px',
                                    cursor: 'pointer'
                                }}
                            >
                                {isRTL() ? 'تجاهل' : 'Decline'}
                            </button>
                        </div>
                    </div>
                ) : friendshipStatus.status === 'PENDING_SENT' ? (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px',
                        margin: '6px auto 10px',
                        padding: '5px 14px',
                        borderRadius: '20px',
                        backgroundColor: 'rgba(59, 130, 246, 0.12)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        fontSize: '11px',
                        color: '#60a5fa',
                        width: 'fit-content'
                    }}>
                        <span>⏳</span>
                        <span>{isRTL() ? 'طلب الصداقة معلق • الاتصال متاح فور قبول الطرف الآخر' : 'Friend request pending • Calling unlocks upon acceptance'}</span>
                    </div>
                ) : (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        margin: '8px 12px 12px',
                        padding: '8px 14px',
                        borderRadius: '12px',
                        backgroundColor: 'rgba(15, 23, 42, 0.85)',
                        border: '1px solid rgba(212, 175, 55, 0.25)',
                        fontSize: '11.5px',
                        color: '#94a3b8'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                            <Lock size={14} color="#d4af37" />
                            <span>{isRTL() ? 'المكالمات مقفلة حتى تتبادلا طلب الصداقة' : 'Calls locked until you become mutual friends'}</span>
                        </div>
                        <button
                            onClick={handleSendFriendRequest}
                            disabled={friendshipLoading}
                            style={{
                                background: 'linear-gradient(135deg, #d4af37, #b89327)',
                                color: '#000',
                                border: 'none',
                                borderRadius: '6px',
                                padding: '5px 12px',
                                fontSize: '11px',
                                fontWeight: 700,
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '5px'
                            }}
                        >
                            <UserPlus size={13} />
                            <span>{isRTL() ? 'إرسال طلب صداقة' : 'Add Friend'}</span>
                        </button>
                    </div>
                )}

                {c2cMessages.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '30px 16px', color: '#94A3B8' }}>
                        <p style={{ fontSize: '13px', margin: '0 0 10px 0', color: '#CBD5E1' }}>
                            {isRTL() ? `بدأت الآن محادثة خاصة مع ${activeC2cSession.recipientName}` : `Started direct chat with ${activeC2cSession.recipientName}`}
                        </p>
                        <span style={{ fontSize: '11.5px', color: '#64748B' }}>
                            {isRTL() ? 'اكتب رسالة أو شارك منتجاً باستخدام زر الحقيبة 🛍️' : 'Send a message or share a product using the bag icon 🛍️'}
                        </span>
                    </div>
                )}

                {c2cMessages.map((msg) => {
                    const mine = Number(msg.senderId) === Number(customerId) || msg.senderType === 'customer' && Number(msg.senderId) === Number(customerId) || String(msg.id).startsWith('c2c-temp-');
                    const text = msg.message || msg.content || '';
                    const timeStr = msg.createdAt || msg.timestamp 
                        ? new Date(msg.createdAt || msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) 
                        : '';

                    const contentNode = renderUniversalChatContent(text, mine);

                    return (
                        <div key={msg.id || `${msg.timestamp}-${text}`} className={`chat-bubble ${mine ? 'mine' : 'theirs'}`}>
                            {contentNode}
                            {timeStr && (
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 3, fontSize: '10px', color: mine ? '#667781' : '#94a3b8', marginTop: 3, direction: 'ltr' }}>
                                    <span>{timeStr}</span>
                                    {mine && <CheckCheck size={13} color="#53bdeb" />}
                                </div>
                            )}
                        </div>
                    );
                })}
            </>
        );
    };

    const renderC2cBody = () => {
        if (!isAuthenticated) {
            return (
                <div style={{
                    padding: '32px 16px',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    gap: '12px'
                }}>
                    <div style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        background: 'rgba(212, 175, 55, 0.12)',
                        border: '1.5px solid rgba(212, 175, 55, 0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 24px rgba(212, 175, 55, 0.2)'
                    }}>
                        <Users size={28} color="#D4AF37" />
                    </div>
                    <h3 style={{ margin: 0, color: '#FFFFFF', fontSize: '16px', fontWeight: '800' }}>
                        {isRTL() ? 'محادثات العملاء المباشرة' : 'Customer Community Chats'}
                    </h3>
                    <p style={{ margin: 0, color: '#94A3B8', fontSize: '12.5px', lineHeight: 1.6, maxWidth: '280px' }}>
                        {isRTL() 
                            ? 'تواصل مباشرة مع نخبة عملاء LUXVONO، شاركهم آرائك واستفساراتك وأرقى المنتجات.'
                            : 'Connect directly with LUXVONO VIP customers, share insights, recommendations, and exclusive products.'}
                    </p>
                    <button
                        type="button"
                        onClick={() => navigate('/login')}
                        style={{
                            marginTop: '8px',
                            background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                            border: 'none',
                            color: '#000000',
                            fontWeight: '800',
                            fontSize: '13px',
                            padding: '10px 24px',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            boxShadow: '0 4px 16px rgba(212, 175, 55, 0.35)'
                        }}
                    >
                        {isRTL() ? 'تسجيل الدخول للبدء' : 'Sign In to Chat'}
                    </button>
                </div>
            );
        }

        if (activeC2cSession) {
            return renderC2cConversation();
        }

        return renderC2cThreadsList();
    };


    const renderAiBody = () => (
        <>
            {/* Sovereign AI Welcome Card */}
            {aiMessages.length <= 1 && (
                <div className="chat-welcome-card">
                    <div className="chat-welcome-badge">
                        <Sparkles size={13} /> {isRTL() ? 'الذكاء الاصطناعي السيادي' : 'Sovereign AI Core'}
                    </div>
                    <h3 className="chat-welcome-title">
                        {isRTL() ? 'المستشار الرقمي الفاخر | LUXVONO' : 'LUXVONO Luxury Digital Advisor'}
                    </h3>
                    <p className="chat-welcome-desc">
                        {isRTL() 
                            ? 'مساعدك الذكي المتخصص لاكتشاف المقتنيات النادرة، مقارنة المواصفات، والتحقق من الأسعار والشحن الدولي.'
                            : 'Your specialized AI concierge for fine horology, bespoke jewelry, order tracking, and worldwide logistics.'}
                    </p>

                    <div className="chat-starter-prompts">
                        {[
                            { text: isRTL() ? '⌚ أرقى الساعات السويسرية وأوديمار بيغيه' : '⌚ Swiss Haute Horlogerie & Audemars Piguet', label: isRTL() ? 'ساعات أوديمار بيغيه' : 'Audemars Piguet watches' },
                            { text: isRTL() ? '👜 حقائب هيرميس الأصلية ومجموعات كارتييه' : '👜 Authentic Hermès Bags & Cartier', label: isRTL() ? 'حقائب هيرميس الأصلية' : 'Hermes Birkin bags' },
                            { text: isRTL() ? '🛡️ شهادات الأصالة وضمان الجودة 100%' : '🛡️ Authenticity & 100% Quality Guarantees', label: isRTL() ? 'هل المنتجات أصلية؟' : 'Are products authentic?' },
                            { text: isRTL() ? '🚚 تفاصيل الشحن الفاخر والتوصيل السريع' : '🚚 Worldwide Express Luxury Shipping', label: isRTL() ? 'ما هي مدة وتكلفة الشحن؟' : 'Shipping and delivery times' }
                        ].map((prompt, idx) => (
                            <button
                                key={idx}
                                type="button"
                                className="chat-starter-prompt-btn"
                                onClick={() => sendAi(prompt.label)}
                            >
                                <span>{prompt.text}</span>
                                {isRTL() ? <ChevronLeft size={14} opacity={0.6} /> : <ChevronRight size={14} opacity={0.6} />}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {aiMessages.map((msg) => (
                <div key={msg.id} className={`chat-bubble ${msg.role === 'user' ? 'mine' : 'theirs'}`}>
                    {msg.role !== 'user' && (
                        <div className="chat-bubble-header">
                            <span className="ai-bubble-tag">
                                <Sparkles size={12} /> {isRTL() ? 'المستشار الذكي' : 'Digital Advisor'}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <button
                                    type="button"
                                    className="chat-voice-btn"
                                    onClick={() => speakAiText(msg.text, msg.id)}
                                    title={isPlayingAiVoiceId === msg.id ? (isRTL() ? 'إيقاف الصوت' : 'Stop Audio') : (isRTL() ? 'استماع للصوت' : 'Listen with Voice')}
                                    style={{
                                        background: 'transparent',
                                        border: 'none',
                                        color: isPlayingAiVoiceId === msg.id ? '#D4AF37' : '#94a3b8',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        padding: '2px 4px'
                                    }}
                                >
                                    {isPlayingAiVoiceId === msg.id ? <VolumeX size={14} className="animate-pulse" /> : <Volume2 size={14} />}
                                </button>
                                <button
                                    type="button"
                                    className="chat-copy-btn"
                                    onClick={() => handleCopy(msg.id, msg.text)}
                                    title={isRTL() ? 'نسخ الإجابة' : 'Copy'}
                                >
                                    {copiedMsgId === msg.id ? <Check size={13} color="#10B981" /> : <Copy size={13} />}
                                </button>
                            </div>
                        </div>
                    )}
                    <div style={{ lineHeight: 1.6 }}>
                        {msg.role === 'user' ? (
                            <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>
                        ) : (
                            formatAiMarkdown(msg.text)
                        )}
                    </div>

                    {/* Interactive Product Showcase Cards */}
                    {Array.isArray(msg.products) && msg.products.length > 0 && (
                        <div className="chat-card-grid">
                            {msg.products.map((product) => (
                                <div key={product.id} className="chat-product-card">
                                    <img 
                                        src={resolveStorefrontImageUrl(product.imageUrl) || 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80'} 
                                        alt={product.name || 'Product'} 
                                        className="chat-product-thumb"
                                        loading="lazy" 
                                        onError={(e) => {
                                            e.currentTarget.onerror = null;
                                            e.currentTarget.src = 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?auto=format&fit=crop&w=600&q=80';
                                        }}
                                    />
                                    <div className="chat-product-meta">
                                        <div className="chat-product-title">{product.name}</div>
                                        <div className="chat-product-price-row">
                                            <span className="chat-product-price">${Number(product.price || 0).toLocaleString()}</span>
                                            {product.badge && <span className="chat-product-badge">{product.badge}</span>}
                                        </div>
                                        <div className="chat-product-actions">
                                            <button
                                                type="button"
                                                className="chat-product-btn-add"
                                                onClick={() => {
                                                    navigate(product.productUrl || `/product/${product.id}`);
                                                    closeChat();
                                                }}
                                            >
                                                <ExternalLink size={12} /> {isRTL() ? 'عرض التفاصيل' : 'View Item'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Quick Action Pills */}
                    {Array.isArray(msg.quickActions) && msg.quickActions.length > 0 && (
                        <div className="chat-quick-actions-wrap">
                            {msg.quickActions.map((action, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    className="chat-quick-action-btn"
                                    onClick={() => {
                                        if (action.action === 'NAVIGATE') {
                                            navigate(action.payload || '/');
                                            closeChat();
                                        } else {
                                            sendSupport(action.label);
                                        }
                                    }}
                                >
                                    <span>{action.label}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ))}

            {aiTyping && (
                <div className="chat-typing">
                    <span className="typing-dots"><span></span><span></span><span></span></span>
                    <span>{isRTL() ? 'المستشار الذكي يحلل طلبك الفاخر...' : 'AI Advisor is analyzing...'}</span>
                </div>
            )}
        </>
    );

    return (
        <div className={`chat-widget ${isOpen ? 'open chat-window-opened' : ''} ${isFabHidden && !isOpen ? 'fab-hidden' : ''} ${isMobileFullscreen ? 'mobile-fullscreen' : ''}`}>
            {!isOpen && (
                <div className="chat-fab-wrap">
                    <div className="chat-fab-glow-ring" />
                    <button 
                        className="chat-fab" 
                        onClick={() => {
                            setIsSellerMode(false);
                            setActiveTab(SUPPORT_TAB);
                            setIsOpen(true);
                            loadSupportChat(false);
                        }} 
                        aria-label={isRTL() ? 'الدعم الفني المباشر' : 'Live Technical Support'}
                    >
                        <Headphones size={26} color="#ffffff" />
                        <span className="chat-fab-badge">
                            <span className="live-status-pulse" /> LIVE
                        </span>
                    </button>
                </div>
            )}

            {isOpen && (
                <div className="chat-window">
                    {/* Header */}
                    <div className="chat-header">
                        <div className="chat-header-info">
                            {activeTab === C2C_TAB && activeC2cSession ? (
                                <button
                                    type="button"
                                    className="chat-back-to-threads-btn"
                                    onClick={handleBackToConversations}
                                    title={isRTL() ? 'الرجوع إلى قائمة المحادثات' : 'Back to conversations'}
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(212, 175, 55, 0.15)',
                                        border: '1px solid rgba(212, 175, 55, 0.35)',
                                        color: '#D4AF37',
                                        borderRadius: '8px',
                                        padding: '5px 9px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        gap: '4px',
                                        marginRight: isRTL() ? '0' : '6px',
                                        marginLeft: isRTL() ? '6px' : '0'
                                    }}
                                >
                                    {isRTL() ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
                                    <span>{isRTL() ? 'المحادثات' : 'Chats'}</span>
                                </button>
                            ) : (
                                <button type="button" className="chat-mobile-back-btn" onClick={closeChat} title="Back">
                                    {isRTL() ? <ArrowRight size={20} /> : <ArrowLeft size={20} />}
                                </button>
                            )}

                            <div className="chat-header-avatar" style={
                                activeTab === C2C_TAB 
                                    ? { background: 'rgba(212, 175, 55, 0.15)', borderColor: '#D4AF37' } 
                                    : (isSellerMode ? { background: 'rgba(16, 185, 129, 0.15)', borderColor: '#10B981' } : {})
                            }>
                                {activeTab === C2C_TAB ? (
                                    activeC2cSession?.recipientAvatar ? (
                                        <img src={activeC2cSession.recipientAvatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                    ) : (
                                        <Users size={20} color="#D4AF37" />
                                    )
                                ) : isSellerMode ? (
                                    productContext?.sellerAvatar ? (
                                        <img src={productContext.sellerAvatar} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                    ) : (
                                        <Store size={20} color="#10B981" />
                                    )
                                ) : (
                                    <Headphones size={22} color="#FE2C55" />
                                )}
                                <span className="avatar-online-dot" />
                            </div>
                            <div className="chat-header-text">
                                <h4 className="chat-header-title" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <span style={{ maxWidth: '170px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {activeTab === C2C_TAB 
                                            ? (activeC2cSession?.recipientName || (isRTL() ? 'محادثات العملاء' : 'Customer Chats'))
                                            : (isSellerMode ? (productContext?.sellerName || (isRTL() ? 'صاحب المتجر المعتمد' : 'Verified Merchant')) : (isRTL() ? 'الدعم الفني المباشر' : 'Direct Live Support'))}
                                    </span>
                                    {activeTab === C2C_TAB && activeC2cSession && (
                                        <span style={{ background: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37', border: '0.5px solid rgba(212, 175, 55, 0.5)', fontSize: '9px', fontWeight: '800', padding: '1px 5px', borderRadius: '4px', flexShrink: 0 }}>
                                            VIP C2C
                                        </span>
                                    )}
                                    {activeTab !== C2C_TAB && isSellerMode && (
                                        <span style={{ background: '#10B981', color: '#fff', fontSize: '9px', fontWeight: '800', padding: '1px 5px', borderRadius: '4px', flexShrink: 0 }}>
                                            SELLER
                                        </span>
                                    )}
                                </h4>
                                <span className="chat-header-status">
                                    <span className="live-status-pulse" />
                                    {activeTab === C2C_TAB
                                        ? (activeC2cSession ? (activeC2cSession.recipientUsername ? `@${activeC2cSession.recipientUsername}` : (isRTL() ? 'متصل الآن' : 'Online')) : (isRTL() ? 'تواصل مباشر بين العملاء' : 'Direct Customer Network'))
                                        : (isSellerMode 
                                            ? (isRTL() ? '⚡ رد فوري من المتجر' : '⚡ Instant Seller Chat')
                                            : (isRTL() ? 'خدمة عملاء حية ومباشرة' : 'Live Customer Specialist'))}
                                    <span className="latency-badge" style={{ color: '#10B981', borderColor: 'rgba(16, 185, 129, 0.3)', background: 'rgba(16, 185, 129, 0.1)' }}>
                                        ● {isRTL() ? 'متصل' : 'Online'}
                                    </span>
                                </span>
                            </div>
                        </div>

                        <div className="chat-header-actions">
                            <button 
                                type="button" 
                                onClick={() => setSoundEnabled(!soundEnabled)} 
                                title={soundEnabled ? 'Mute' : 'Unmute'}
                            >
                                {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
                            </button>
                            {(() => {
                                const canCallPeer = Boolean(friendshipStatus?.isFriend || friendshipStatus?.friend || friendshipStatus?.canCall || friendshipStatus?.status === 'ACCEPTED');
                                return (
                                    <>
                                        <button 
                                            type="button" 
                                            title={activeTab === C2C_TAB && !canCallPeer ? (isRTL() ? 'المكالمات مقفلة (تتطلب صداقة متبادلة)' : 'Calls Locked (Friends Only)') : 'Video Call'} 
                                            style={{ position: 'relative' }}
                                            onClick={async () => {
                                                if (activeTab === C2C_TAB) {
                                                    if (!activeC2cSession) return;
                                                    if (!canCallPeer) {
                                                        setShowFriendshipModal(true);
                                                        return;
                                                    }
                                                    const peerId = activeC2cSession.recipientUserId || activeC2cSession.recipientId;
                                                    const peerName = activeC2cSession.recipientName || 'Friend';
                                                    setCallType('video');
                                                    setRemoteName(peerName);
                                                    setCallStatus('calling');
                                                    window.dispatchEvent(new CustomEvent('START_OUTGOING_CALL', { 
                                                        detail: { 
                                                            type: 'video', 
                                                            targetSessionId: activeC2cSession.sessionId,
                                                            recipientId: peerId,
                                                            remoteName: peerName
                                                        } 
                                                    }));
                                                    return;
                                                }

                                                let targetSid = sessionId;
                                                if (!targetSid && customerId) {
                                                    try {
                                                        const conv = await getSupportConversation(customerId);
                                                        if (conv?.sessionId) {
                                                            targetSid = conv.sessionId;
                                                            setSessionId(conv.sessionId);
                                                        }
                                                    } catch (e) {
                                                        console.error('Failed to init session for video call', e);
                                                    }
                                                }
                                                setCallType('video');
                                                setRemoteName(isSellerMode ? (productContext?.sellerName || (isRTL() ? 'صاحب المتجر' : 'Store Owner')) : (isRTL() ? 'فريق الدعم المباشر' : 'Support Specialist'));
                                                setCallStatus('calling');
                                                window.dispatchEvent(new CustomEvent('START_OUTGOING_CALL', { 
                                                    detail: { 
                                                        type: 'video', 
                                                        targetSessionId: targetSid,
                                                        remoteName: isSellerMode ? productContext?.sellerName : undefined
                                                    } 
                                                }));
                                            }}
                                        >
                                            <Video size={16} />
                                            {activeTab === C2C_TAB && !canCallPeer && (
                                                <span style={{
                                                    position: 'absolute',
                                                    bottom: '1px',
                                                    right: '1px',
                                                    width: '7px',
                                                    height: '7px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#EF4444',
                                                    border: '1px solid #1E293B'
                                                }} title={isRTL() ? 'مقفلة: تتطلب طلب صداقة مشترك' : 'Locked: Requires mutual friendship'} />
                                            )}
                                        </button>
                                        <button 
                                            type="button" 
                                            title={activeTab === C2C_TAB && !canCallPeer ? (isRTL() ? 'المكالمات مقفلة (تتطلب صداقة متبادلة)' : 'Calls Locked (Friends Only)') : 'Audio Call'} 
                                            style={{ position: 'relative' }}
                                            onClick={async () => {
                                                if (activeTab === C2C_TAB) {
                                                    if (!activeC2cSession) return;
                                                    if (!canCallPeer) {
                                                        setShowFriendshipModal(true);
                                                        return;
                                                    }
                                                    const peerId = activeC2cSession.recipientUserId || activeC2cSession.recipientId;
                                                    const peerName = activeC2cSession.recipientName || 'Friend';
                                                    setCallType('audio');
                                                    setRemoteName(peerName);
                                                    setCallStatus('calling');
                                                    window.dispatchEvent(new CustomEvent('START_OUTGOING_CALL', { 
                                                        detail: { 
                                                            type: 'audio', 
                                                            targetSessionId: activeC2cSession.sessionId,
                                                            recipientId: peerId,
                                                            remoteName: peerName
                                                        } 
                                                    }));
                                                    return;
                                                }

                                                let targetSid = sessionId;
                                                if (!targetSid && customerId) {
                                                    try {
                                                        const conv = await getSupportConversation(customerId);
                                                        if (conv?.sessionId) {
                                                            targetSid = conv.sessionId;
                                                            setSessionId(conv.sessionId);
                                                        }
                                                    } catch (e) {
                                                        console.error('Failed to init session for audio call', e);
                                                    }
                                                }
                                                setCallType('audio');
                                                setRemoteName(isSellerMode ? (productContext?.sellerName || (isRTL() ? 'صاحب المتجر' : 'Store Owner')) : (isRTL() ? 'فريق الدعم المباشر' : 'Support Specialist'));
                                                setCallStatus('calling');
                                                window.dispatchEvent(new CustomEvent('START_OUTGOING_CALL', { 
                                                    detail: { 
                                                        type: 'audio', 
                                                        targetSessionId: targetSid,
                                                        remoteName: isSellerMode ? productContext?.sellerName : undefined
                                                    } 
                                                }));
                                            }}
                                        >
                                            <Phone size={16} />
                                            {activeTab === C2C_TAB && !canCallPeer && (
                                                <span style={{
                                                    position: 'absolute',
                                                    bottom: '1px',
                                                    right: '1px',
                                                    width: '7px',
                                                    height: '7px',
                                                    borderRadius: '50%',
                                                    backgroundColor: '#EF4444',
                                                    border: '1px solid #1E293B'
                                                }} title={isRTL() ? 'مقفلة: تتطلب طلب صداقة مشترك' : 'Locked: Requires mutual friendship'} />
                                            )}
                                        </button>
                                        {activeTab === C2C_TAB && activeC2cSession && (
                                            (activeC2cSession.isBlocked || friendshipStatus?.isBlocked || friendshipStatus?.status === 'BLOCKED') ? (
                                                <button
                                                    type="button"
                                                    onClick={handleUnblockPeer}
                                                    disabled={friendshipLoading}
                                                    title={isRTL() ? 'فك الحظر عن هذا الحساب' : 'Unblock Account'}
                                                    style={{
                                                        background: 'rgba(16, 185, 129, 0.18)',
                                                        border: '1px solid rgba(16, 185, 129, 0.45)',
                                                        color: '#10B981',
                                                        borderRadius: '8px',
                                                        padding: '4px 8px',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <ShieldCheck size={14} />
                                                    <span>{isRTL() ? 'فك الحظر' : 'Unblock'}</span>
                                                </button>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={handleBlockPeer}
                                                    disabled={friendshipLoading}
                                                    title={isRTL() ? 'حظر هذا الحساب' : 'Block Account'}
                                                    style={{
                                                        background: 'rgba(239, 68, 68, 0.14)',
                                                        border: '1px solid rgba(239, 68, 68, 0.35)',
                                                        color: '#EF4444',
                                                        borderRadius: '8px',
                                                        padding: '4px 8px',
                                                        fontSize: '11px',
                                                        fontWeight: 700,
                                                        cursor: 'pointer',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '4px'
                                                    }}
                                                >
                                                    <UserX size={14} />
                                                    <span>{isRTL() ? 'حظر' : 'Block'}</span>
                                                </button>
                                            )
                                        )}
                                    </>
                                );
                            })()}
                            <button type="button" title="Close" onClick={closeChat}>
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Support / AI Concierge Navigation (Shown ONLY in Technical Support, never in Customer-Seller messaging) */}
                    {activeTab !== C2C_TAB && (
                        <div className="chat-nav-tabs">
                            <button
                                type="button"
                                className={`chat-nav-tab ${activeTab === SUPPORT_TAB ? 'active' : ''}`}
                                onClick={() => {
                                    setActiveTab(SUPPORT_TAB);
                                    loadSupportChat(false);
                                }}
                            >
                                <Headphones size={14} />
                                <span>{isSellerMode ? (isRTL() ? 'محادثة المتجر' : 'Seller Chat') : (isRTL() ? 'الدعم المباشر' : 'Live Support')}</span>
                            </button>

                            <button
                                type="button"
                                className={`chat-nav-tab ${activeTab === AI_TAB ? 'active' : ''}`}
                                onClick={() => setActiveTab(AI_TAB)}
                            >
                                <Sparkles size={14} />
                                <span>{isRTL() ? 'المستشار الذكي' : 'AI Concierge'}</span>
                            </button>
                        </div>
                    )}

                    {/* Live Support / Seller Sub-Bar */}
                    {activeTab === SUPPORT_TAB && (
                    <div className="chat-support-badge-bar" style={{
                        background: isSellerMode ? 'rgba(16, 185, 129, 0.08)' : 'rgba(254, 44, 85, 0.05)',
                        borderBottom: isSellerMode ? '1px solid rgba(16, 185, 129, 0.22)' : '1px solid rgba(254, 44, 85, 0.18)',
                        padding: '8px 16px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        fontSize: '11.5px',
                        fontWeight: 600,
                        color: '#ffffff'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {isSellerMode ? <Store size={14} color="#10B981" /> : <ShieldCheck size={14} color="#10B981" />}
                            <span>
                                {isSellerMode 
                                    ? (isRTL() ? 'محادثة مباشرة مع صاحب المتجر والبائع' : 'Direct live chat with verified store owner')
                                    : (isRTL() ? 'دعم فني مباشر متصل الآن • موظفون حقيقيون' : 'Direct Live Support • Real Human Specialists')}
                            </span>
                        </div>
                        {isSellerMode ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setIsSellerMode(false);
                                    setProductContext(null);
                                    loadSupportChat(false);
                                }}
                                style={{
                                    background: 'rgba(255,255,255,0.08)',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    color: '#f8fafc',
                                    fontSize: '10.5px',
                                    padding: '2px 8px',
                                    borderRadius: '6px',
                                    cursor: 'pointer'
                                }}
                            >
                                {isRTL() ? 'التحويل للدعم العام' : 'Switch to Support'}
                            </button>
                        ) : (
                            <span style={{ fontSize: '10px', color: '#10B981', background: 'rgba(16, 185, 129, 0.12)', border: '1px solid rgba(16, 185, 129, 0.3)', padding: '1px 6px', borderRadius: '8px' }}>
                                24/7 LIVE
                            </span>
                        )}
                    </div>
                    )}

                    {/* Active Product Context Card (When opened via "Ask Seller") */}
                    {productContext && (
                        <div style={{
                            background: '#0b0f19',
                            borderBottom: '1px solid rgba(212,175,55,0.3)',
                            padding: '10px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '10px'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                {productContext.productImage ? (
                                    <img 
                                        src={productContext.productImage} 
                                        alt="" 
                                        style={{ width: '40px', height: '40px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #334155' }} 
                                    />
                                ) : (
                                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <ShoppingBag size={18} color="#d4af37" />
                                    </div>
                                )}
                                <div style={{ minWidth: 0 }}>
                                    <strong style={{ display: 'block', color: '#f8fafc', fontSize: '12px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {productContext.productName}
                                    </strong>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#94a3b8' }}>
                                        <span style={{ color: '#d4af37', fontWeight: '700' }}>{productContext.productPrice} {productContext.productCurrency}</span>
                                        <span>• {productContext.sellerName}</span>
                                    </div>
                                </div>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/product/${productContext.productId}`)}
                                    style={{
                                        background: 'rgba(212,175,55,0.15)',
                                        border: '1px solid rgba(212,175,55,0.3)',
                                        color: '#d4af37',
                                        padding: '4px 8px',
                                        borderRadius: '6px',
                                        fontSize: '11px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '3px'
                                    }}
                                >
                                    <span>{isRTL() ? 'عرض المنتج' : 'View'}</span>
                                    <ExternalLink size={11} />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setProductContext(null)}
                                    style={{ background: 'transparent', border: 'none', color: '#64748b', cursor: 'pointer', padding: '4px' }}
                                    title="Dismiss"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Conversation Body */}
                    <div className={`chat-body ${activeTab === SUPPORT_TAB || activeTab === C2C_TAB ? 'whatsapp-mode' : ''}`}>
                        {activeTab === C2C_TAB
                            ? renderC2cBody()
                            : (activeTab === SUPPORT_TAB ? renderSupportBody() : renderAiBody())}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Quick Inquiry Chips when product context is active */}
                    {productContext && (
                        <div style={{ display: 'flex', gap: '6px', padding: '6px 14px', background: '#0b0f19', borderTop: '1px solid #334155', overflowX: 'auto' }}>
                            {[
                                isRTL() ? 'هل المنتج متوفر للشحن الفوري؟ 📦' : 'Is this item in stock? 📦',
                                isRTL() ? 'كم يستغرق التوصيل إلى مدينتي؟ ⚡' : 'How long is delivery? ⚡',
                                isRTL() ? 'هل المنتج أصلي ومعه الضمان؟ 🛡️' : 'Is this authentic with warranty? 🛡️'
                            ].map((q, idx) => (
                                <button
                                    key={idx}
                                    type="button"
                                    onClick={() => {
                                        setInput(q);
                                        chatInputRef.current?.focus();
                                    }}
                                    style={{
                                        background: '#1e293b',
                                        border: '1px solid #334155',
                                        color: '#cbd5e1',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: '11px',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {q}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Capsule Input Bar */}
                    <div className="chat-input-wrapper" style={{ position: 'relative' }}>
                        {showStickers && (
                            <div className="sticker-picker" style={{ position: 'absolute', bottom: '100%', right: '0', background: 'var(--surface, #1e293b)', padding: '12px', borderRadius: '12px', border: '1px solid var(--border, rgba(255,255,255,0.1))', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', zIndex: 100, boxShadow: '0 8px 32px rgba(0,0,0,0.5)', backdropFilter: 'blur(10px)' }}>
                                {STICKERS.map(emoji => (
                                    <button key={emoji} type="button" onClick={() => sendSticker(emoji)} style={{ background: 'transparent', border: 'none', fontSize: '26px', cursor: 'pointer', padding: '6px', borderRadius: '8px', transition: 'transform 0.15s ease' }} onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.25)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}>
                                        {emoji}
                                    </button>
                                ))}
                            </div>
                        )}
                        <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileUpload} />
                        
                        {isAiListeningMic ? (
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px', padding: '6px 12px', background: 'rgba(212, 175, 55, 0.15)', borderRadius: '24px', border: '1px solid rgba(212, 175, 55, 0.4)' }}>
                                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#D4AF37', animation: 'pulseRecording 1s infinite' }} />
                                <span style={{ color: '#D4AF37', fontWeight: 'bold', fontSize: '13px', flex: 1 }}>
                                    {isRTL() ? '🎙️ المستشار يستمع لصوتك الآن... تحدث' : '🎙️ AI is listening to your voice... Speak now'}
                                </span>
                                <button type="button" onClick={toggleAiSpeechRecognition} title="Done" style={{ background: '#D4AF37', border: 'none', color: '#000', borderRadius: '50%', width: '28px', height: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontWeight: 'bold' }}>
                                    <Check size={16} />
                                </button>
                            </div>
                        ) : isRecording ? (
                            <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '8px', padding: '6px 10px', background: 'rgba(239, 68, 68, 0.1)', borderRadius: '24px', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
                                <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#ef4444', animation: 'pulseRecording 1s infinite' }} />
                                <span style={{ color: '#ef4444', fontWeight: 'bold', fontSize: '13px', flex: 1 }}>
                                    {isRTL() ? `جاري التسجيل... ${recordingTime} ثانية` : `Recording... ${recordingTime}s`}
                                </span>
                                <button type="button" onClick={() => stopRecording(true)} title="Cancel" style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '6px' }}>
                                    <Trash2 size={18} />
                                </button>
                                <button type="button" onClick={() => stopRecording(false)} title="Send Voice Note" style={{ background: '#ef4444', border: 'none', color: '#fff', borderRadius: '50%', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <Send size={16} />
                                </button>
                            </div>
                        ) : (activeTab === C2C_TAB && activeC2cSession && (activeC2cSession.isBlocked || friendshipStatus?.isBlocked || friendshipStatus?.status === 'BLOCKED')) ? (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                width: '100%',
                                gap: '10px',
                                padding: '8px 14px',
                                background: 'rgba(239, 68, 68, 0.12)',
                                borderRadius: '14px',
                                border: '1px solid rgba(239, 68, 68, 0.35)'
                            }}>
                                <span style={{ color: '#FCA5A5', fontWeight: 700, fontSize: '12px' }}>
                                    {isRTL() ? '🚫 هذه المحادثة محظورة حالياً' : '🚫 This conversation is currently blocked'}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleUnblockPeer}
                                    disabled={friendshipLoading}
                                    style={{
                                        background: '#16A34A',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        borderRadius: '8px',
                                        padding: '6px 14px',
                                        fontSize: '12px',
                                        fontWeight: 800,
                                        cursor: 'pointer'
                                    }}
                                >
                                    {isRTL() ? 'فك الحظر' : 'Unblock'}
                                </button>
                            </div>
                        ) : (
                            <form className="chat-input" onSubmit={handleSend} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <button type="button" className="media-btn" onClick={() => fileInputRef.current?.click()} title={isRTL() ? 'إرفاق صورة' : 'Upload Image'} disabled={isUploading || (activeTab === C2C_TAB && !activeC2cSession)}>
                                    <ImageIcon size={18} />
                                </button>
                                <button type="button" className="media-btn product-btn" onClick={() => setShowProductShareModal(true)} title={isRTL() ? 'مشاركة منتج في المحادثة' : 'Share Product in Chat'} disabled={activeTab === C2C_TAB && !activeC2cSession}>
                                    <ShoppingBag size={18} />
                                </button>
                                <button type="button" className="media-btn" onClick={startRecording} title={isRTL() ? 'تسجيل رسالة صوتية' : 'Record Voice Note'} disabled={isUploading || (activeTab === C2C_TAB && !activeC2cSession)}>
                                    <Mic size={18} />
                                </button>
                                <button type="button" className="media-btn" onClick={() => setShowStickers(!showStickers)} title={isRTL() ? 'ملصقات' : 'Send Sticker'} disabled={activeTab === C2C_TAB && !activeC2cSession}>
                                    <Smile size={18} />
                                </button>
                                <input
                                    ref={chatInputRef}
                                    type="text"
                                    placeholder={
                                        activeTab === C2C_TAB 
                                            ? (activeC2cSession 
                                                ? (isRTL() ? `اكتب رسالتك إلى ${activeC2cSession.recipientName}...` : `Message ${activeC2cSession.recipientName}...`)
                                                : (isRTL() ? 'اختر محادثة لبدء المراسلة...' : 'Select a chat to message...'))
                                            : (isRTL() ? 'اكتب رسالتك لفريق الدعم الفني المباشر...' : 'Type your message to Live Support...')
                                    }
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    disabled={isUploading || (activeTab === C2C_TAB && !activeC2cSession)}
                                    style={{ flex: 1 }}
                                />

                                <button 
                                    type="submit" 
                                    className="chat-send-btn"
                                    onPointerDown={(e) => {
                                        // Prevents mobile virtual keyboard from blurring/collapsing
                                        e.preventDefault();
                                    }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                    }}
                                    disabled={(!input.trim() && !isUploading) || (activeTab === C2C_TAB && !activeC2cSession)}
                                    aria-label="Send"
                                >
                                    <Send size={17} style={{ transform: isRTL() ? 'rotate(180deg)' : 'none' }} />
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            <WebRTCCallOverlay
                sessionId={sessionId}
                customerId={customerId}
                customerName={user ? (user.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : (user.username || user.email || 'VIP Customer')) : 'VIP Customer'}
                stompClient={stompClientInstance || stompClientRef.current || window.__LUXVONO_STOMP_CLIENT}
                currentStatus={callStatus}
                setCurrentStatus={setCallStatus}
                callType={callType}
                setCallType={setCallType}
                remoteName={remoteName}
                setRemoteName={setRemoteName}
            />

            {/* Mutual Friendship & Calling Permission Modal */}
            {showFriendshipModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.82)',
                        backdropFilter: 'blur(8px)',
                        zIndex: 100070,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                    }}
                    onClick={() => setShowFriendshipModal(false)}
                >
                    <div
                        style={{
                            background: '#0B0F19',
                            border: '1px solid rgba(212, 175, 55, 0.4)',
                            borderRadius: '20px',
                            width: '100%',
                            maxWidth: '420px',
                            boxShadow: '0 25px 60px rgba(0,0,0,0.85), 0 0 30px rgba(212, 175, 55, 0.15)',
                            padding: '24px',
                            textAlign: 'center',
                            position: 'relative',
                            color: '#F8FAFC'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowFriendshipModal(false)}
                            style={{
                                position: 'absolute',
                                top: '16px',
                                right: isRTL() ? 'auto' : '16px',
                                left: isRTL() ? '16px' : 'auto',
                                background: 'transparent',
                                border: 'none',
                                color: '#94A3B8',
                                cursor: 'pointer',
                                padding: '4px'
                            }}
                        >
                            <X size={20} />
                        </button>

                        <div style={{
                            width: '64px',
                            height: '64px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(212, 175, 55, 0.25) 0%, rgba(212, 175, 55, 0.05) 70%)',
                            border: '1px solid rgba(212, 175, 55, 0.4)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 16px'
                        }}>
                            <Lock size={30} color="#D4AF37" />
                        </div>

                        <h3 style={{ fontSize: '18px', fontWeight: '800', margin: '0 0 8px 0', color: '#F8FAFC' }}>
                            {isRTL() ? 'المكالمات الصوتية والمرئية مقفلة' : 'Audio & Video Calls Locked'}
                        </h3>

                        <p style={{ fontSize: '13px', lineHeight: '1.6', color: '#94A3B8', margin: '0 0 20px 0' }}>
                            {isRTL() 
                                ? 'للحفاظ على الخصوصية والأمان، المكالمات الصوتية والفيديو بين العملاء متاحة فقط عند تبادل وقبول طلبات الصداقة بين الطرفين.' 
                                : 'For user security and privacy, audio and video calls between customers are only permitted when both members have mutual accepted friend status.'}
                        </p>

                        <div style={{
                            backgroundColor: 'rgba(255, 255, 255, 0.03)',
                            border: '1px solid rgba(255, 255, 255, 0.08)',
                            borderRadius: '12px',
                            padding: '12px',
                            marginBottom: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                        }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                background: '#1E293B',
                                border: '1px solid #D4AF37'
                            }}>
                                {activeC2cSession?.recipientAvatar ? (
                                    <img src={activeC2cSession.recipientAvatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                    <Users size={20} color="#D4AF37" style={{ margin: '8px' }} />
                                )}
                            </div>
                            <div style={{ textAlign: isRTL() ? 'right' : 'left' }}>
                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#F1F5F9' }}>
                                    {activeC2cSession?.recipientName || 'Customer'}
                                </div>
                                <div style={{ fontSize: '11px', color: '#64748B' }}>
                                    {friendshipStatus.status === 'ACCEPTED' || friendshipStatus.isFriend || friendshipStatus.friend
                                        ? (isRTL() ? 'صديق مشترك (المكالمات مفعلة)' : 'Mutual Friend (Calls Unlocked)')
                                        : friendshipStatus.status === 'PENDING_RECEIVED' 
                                            ? (isRTL() ? 'أرسل لك طلب صداقة' : 'Sent you a friend request')
                                            : friendshipStatus.status === 'PENDING_SENT'
                                                ? (isRTL() ? 'طلب الصداقة معلق بانتظار الموافقة' : 'Friend request pending approval')
                                                : (isRTL() ? 'ليس في قائمة أصدقائك بعد' : 'Not yet in your friends list')}
                                </div>
                            </div>
                        </div>

                        {friendshipStatus.status === 'ACCEPTED' || friendshipStatus.isFriend || friendshipStatus.friend ? (
                            <div style={{ textAlign: 'center' }}>
                                <div style={{
                                    padding: '12px',
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(16, 185, 129, 0.12)',
                                    border: '1px solid rgba(16, 185, 129, 0.35)',
                                    fontSize: '13px',
                                    color: '#10B981',
                                    marginBottom: '14px',
                                    fontWeight: '700'
                                }}>
                                    {isRTL() ? '✅ أنتم أصدقاء بالفعل! المكالمات الصوتية والمرئية مفعلة بالكامل.' : '✅ You are mutual friends! Voice and video calls are unlocked.'}
                                </div>
                                <button
                                    onClick={() => {
                                        setShowFriendshipModal(false);
                                        const peerId = activeC2cSession?.recipientUserId || activeC2cSession?.recipientId;
                                        const peerName = activeC2cSession?.recipientName || 'Friend';
                                        setCallType('video');
                                        setRemoteName(peerName);
                                        setCallStatus('calling');
                                        window.dispatchEvent(new CustomEvent('START_OUTGOING_CALL', { 
                                            detail: { 
                                                type: 'video', 
                                                targetSessionId: activeC2cSession?.sessionId,
                                                recipientId: peerId,
                                                remoteName: peerName
                                            } 
                                        }));
                                    }}
                                    style={{
                                        width: '100%',
                                        background: 'linear-gradient(135deg, #10B981, #059669)',
                                        color: '#FFFFFF',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '12px',
                                        fontSize: '13px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <Video size={16} />
                                    <span>{isRTL() ? 'بدء المكالمة الآن 📞' : 'Start Call Now 📞'}</span>
                                </button>
                            </div>
                        ) : friendshipStatus.status === 'PENDING_RECEIVED' ? (
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={handleAcceptFriendRequest}
                                    disabled={friendshipLoading}
                                    style={{
                                        flex: 1,
                                        background: 'linear-gradient(135deg, #D4AF37, #B89327)',
                                        color: '#000',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '12px',
                                        fontSize: '13px',
                                        fontWeight: '700',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px'
                                    }}
                                >
                                    <UserCheck size={16} />
                                    <span>{isRTL() ? 'قبول طلب الصداقة وفتح المكالمات' : 'Accept Request & Unlock Calls'}</span>
                                </button>
                                <button
                                    onClick={handleDeclineFriendRequest}
                                    disabled={friendshipLoading}
                                    style={{
                                        background: 'rgba(255,255,255,0.08)',
                                        color: '#CBD5E1',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: '12px 16px',
                                        fontSize: '13px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {isRTL() ? 'تجاهل' : 'Decline'}
                                </button>
                            </div>
                        ) : friendshipStatus.status === 'PENDING_SENT' ? (
                            <div style={{
                                padding: '12px',
                                borderRadius: '10px',
                                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                fontSize: '12.5px',
                                color: '#60A5FA'
                            }}>
                                {isRTL() ? '⏳ تم إرسال طلب الصداقة. فور قبول الطرف الآخر للطلب، ستتمكنان من الاتصال صوتياً وبفيديو عالي الدقة.' : '⏳ Request already sent. As soon as the other user accepts, both voice and video calls will be enabled.'}
                            </div>
                        ) : (
                            <button
                                onClick={handleSendFriendRequest}
                                disabled={friendshipLoading}
                                style={{
                                    width: '100%',
                                    background: 'linear-gradient(135deg, #D4AF37, #B89327)',
                                    color: '#000',
                                    border: 'none',
                                    borderRadius: '10px',
                                    padding: '12px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)'
                                }}
                            >
                                <UserPlus size={16} />
                                <span>{isRTL() ? 'إرسال طلب صداقة لتمكين الاتصال' : 'Send Friend Request to Enable Calling'}</span>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Product Share Picker Modal */}
            <ProductShareModal
                isOpen={showProductShareModal}
                onClose={() => setShowProductShareModal(false)}
                onShareProduct={handleShareProduct}
            />

            {/* Customer Search & New Chat Modal */}
            <CustomerSearchModal
                isOpen={showCustomerSearchModal}
                onClose={() => setShowCustomerSearchModal(false)}
                onSelectSession={(session) => {
                    setActiveC2cSession(session);
                    activeC2cSessionRef.current = session;
                    window.__luxvono_chat_level = 'thread';
                    setC2cMessages([]);
                    loadC2cMessages(session.sessionId, false);
                    loadC2cThreads(true);
                }}
            />
        </div>
    );
};

export default ChatWidget;
